/**
 * useTorqueProfile.ts
 * FieldIQ — Casing Running / Pipe Profile
 *
 * THE API DECIDES. `GET /torque-profiles/resolve` (API #313) resolves tenant
 * custom first and the API RP 5C1 library second, and returns which one it used.
 * This hook asks and renders the answer. It holds no precedence logic of its own.
 *
 * WHAT THIS REPLACED, and why the old shape was the bug. The hook used to run
 * its own priority order — spec table FIRST for LTC/STC/BTC, tenant profiles
 * never consulted for a standard connection — with `isApiSpec → isEditable =
 * false` making those three read-only. So a tenant could not save a value for a
 * standard connection, and a combination the library does not carry dead-ended:
 * blank fields, no Save button, dashes on the call sheet. TNT hit exactly that
 * with a weight+grade of LT&C the library does not list. Meanwhile the server
 * resolved the same pipe differently, which is how one number reached the screen
 * and another reached the printed document.
 *
 * THE LOCAL SPEC ARRAYS STAY, and they are an OFFLINE FALLBACK ONLY. A crew in a
 * dead zone still has to see library torque. When the resolver is reachable its
 * answer is used and these arrays are not consulted at all — they are a second
 * copy of the library, and a second copy is only safe while it is never the
 * authority. `scripts/check-torque-parity.js` fails the build if they drift from
 * the API's copy.
 *
 * Usage:
 *   const { torque, status, isEditable, saveTorqueProfile, revertToLibrary }
 *     = useTorqueProfile({ size, weightPpf, grade, connectionType });
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { api, type TorqueProfilePayload, type TorqueLookupKey } from '../../api/client';
import { isTubingThread, canonicalizeGrade } from '../../data/casingData';
import { useSyncStore } from '../../lib/offline/sync-store';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ConnectionType =
  // API standard
  | 'LTC'
  | 'STC'
  | 'BTC'
  // Premium — shouldered (low + high shoulder torque apply)
  | 'VAM TOP'
  | 'VAM 21'
  | 'Hunting Wedge'
  | 'Hunting ST-L'
  | 'TenarisHydril Blue'
  | 'TenarisHydril 513'
  | 'NOV RL-4S'
  | 'NOV Wedge 521'
  | 'TMK PF'
  | 'TMK UP'
  // Premium — single shoulder / no shoulder (only min/opt/max)
  | 'Buttress'
  | 'Extreme Line'
  | 'Other Premium'
  | 'Other';

export type CasingGrade =
  | 'H-40'
  | 'J-55'
  | 'K-55'
  | 'M-65'
  | 'N-80'
  | 'L-80'
  | '13Cr L-80'
  | 'C-90'
  | 'C-95'
  | 'T-95'
  | 'P-110'
  | 'Q-125'
  // Sour-service aliases — resolve to base grade for torque lookup
  | 'SOO55'
  | 'PS-80'
  | 'TN 80SS';

/**
 * Where a resolved value came from. Mirrors the API's own `TorqueSource`.
 * `null` means neither the tenant nor the library has a value for this pipe —
 * the caller renders "—" and offers to save one. Never a zero, never a guess.
 */
export type TorqueSource = 'company' | 'api_spec' | null;

/**
 * The labels, mirrored from `src/services/torque-resolver.ts`.
 *
 * ONLINE THESE ARE NOT USED FOR DISPLAY — the API's `sourceLabel` is rendered
 * verbatim, so the screen and the call sheet cannot word it differently. They
 * exist for the offline fallback, which has no server answer to quote, and are
 * spelled identically to the server's constants for that reason. If the API
 * changes its wording, this is the second place to change.
 */
export const COMPANY_TORQUE_LABEL = 'Company value — not API RP 5C1';
export const API_SPEC_TORQUE_LABEL = 'API RP 5C1';

export interface TorqueProfile {
  // Lookup key
  size: string;         // e.g. "5-1/2\""
  weightPpf: number;
  grade: CasingGrade;
  connectionType: ConnectionType;
  // Torque values (ft-lb)
  torqueMin: number | null;
  torqueOptimum: number | null;
  torqueMax: number | null;
  // Shoulder torques — null for non-shouldered connections
  torqueLowShoulder: number | null;
  torqueHighShoulder: number | null;
  /**
   * Whether this combo makes up to a shoulder, as the RESOLVER says (api#830).
   * The tenant's own flag for a company value; for a library value, whether the
   * spec carries shoulder torques; null when nothing was found, which means
   * unknown, not false. Null too from an API that predates the flag and from
   * the offline fallback, which cannot see a company flag.
   */
  shouldered: boolean | null;
  // Metadata
  source: TorqueSource;
  /** Rendered as-is. The API's string online; the mirrored constant offline. */
  sourceLabel: string;
  /**
   * True when this came from the local spec arrays because the resolver could
   * not be reached. The screen says so: an offline value cannot reflect a
   * company override saved on another device, and a crew deciding makeup torque
   * is entitled to know which of the two they are looking at.
   */
  fromOfflineFallback: boolean;
}

export type TorqueStatus =
  | 'idle'       // no size/weight/grade/connection yet
  | 'loading'    // asking the resolver
  | 'resolved'   // the resolver (or the offline fallback) answered
  | 'error';     // the resolver failed AND the offline fallback had nothing

interface UseTorqueProfileOptions {
  size: string;
  weightPpf: number | null;
  grade: CasingGrade | '';
  connectionType: ConnectionType | '';
}

interface UseTorqueProfileReturn {
  torque: TorqueProfile | null;
  status: TorqueStatus;
  /**
   * Whether this combo's shoulder torques apply. The resolved profile's own
   * `shouldered` flag when there is one; otherwise — an unsaved combo, or an
   * answer with no flag — whether the connection is on the known-premium list,
   * matched case-insensitively. It is the DEFAULT for the "Shouldered
   * connection" checkbox, never an override of a saved flag.
   */
  hasShoulders: boolean;
  /**
   * Whether the torque fields accept typing right now. True when the resolver
   * found nothing (the combination is not in the library and the tenant has no
   * value — TNT's case, and it must not dead-end), or when the user has asked
   * to override a library value. NOT a function of connection type: the old
   * `isApiSpec → false` rule is what made LTC/STC/BTC unsavable.
   */
  isEditable: boolean;
  /** True while the user is overriding a value the library does carry. */
  isOverriding: boolean;
  /** Unlock the fields to replace a library value with a company one. */
  beginOverride: () => void;
  /** Abandon an override in progress and go back to the resolved value. */
  cancelOverride: () => void;
  /** True when a company value is what is displayed — i.e. revert is offered. */
  canRevert: boolean;
  /**
   * Save this tenant's value for the current combination.
   *
   * THROWS if the server rejects the save, and updates no state when it does.
   * Callers MUST catch and surface the failure: silently resolving here is the
   * bug this replaced. Pass the caught error to `getApiErrorMessage` to show
   * the API's own reason rather than a generic "Save failed".
   */
  saveTorqueProfile: (values: Partial<TorqueProfile>) => Promise<void>;
  /** Ask the resolver again for the current combination — after a 409, say. */
  refresh: () => Promise<void>;
  /**
   * Delete this tenant's override and go back to the published value
   * (`DELETE /torque-profiles`, API #314 — a hard delete). Re-resolves after,
   * so what lands on screen is the server's answer rather than an assumption
   * about what the library holds.
   *
   * THROWS on failure, like the save, and for the same reason.
   */
  revertToLibrary: () => Promise<void>;
}

// ─── Connection metadata ──────────────────────────────────────────────────────

// `API_SPEC_CONNECTIONS = ['LTC','STC','BTC']` used to live here, and it was the
// bug. It drove `isApiSpec → isEditable = false`, which made those three
// connections read-only: a tenant could not save a value for a standard
// connection, and a combination the library does not carry dead-ended with
// blank fields and no Save button. TNT hit exactly that on an LT&C weight the
// library does not list. Editability is now a function of what the RESOLVER
// found — nothing on file, or an override in progress — never of the connection
// type. Deleted rather than left unused so it cannot be reintroduced by
// autocomplete.

/**
 * Connections with dual-shoulder makeup torque (low + high shoulder).
 *
 * Only a DEFAULT now (api#830). Whether a combo is shouldered is the tenant's
 * flag on the saved profile, set by the "Shouldered connection" checkbox; this
 * list only pre-ticks that box for a combo nothing is on file for. Mirrors
 * KNOWN_SHOULDERED_CONNECTIONS in the API's src/constants/shouldered-connections.ts.
 */
const SHOULDERED_CONNECTIONS: ConnectionType[] = [
  'VAM TOP',
  'VAM 21',
  'Hunting Wedge',
  'Hunting ST-L',
  'TenarisHydril Blue',
  'TenarisHydril 513',
  'NOV RL-4S',
  'NOV Wedge 521',
  'TMK PF',
  'TMK UP',
];

const SHOULDERED_KEYS = new Set(SHOULDERED_CONNECTIONS.map((c) => c.toLowerCase()));

/**
 * Case-insensitive, trimmed, EXACT — the API's own rule. Tenant thread names are
 * stored uppercase ('VAM TOP'), and the old case-sensitive `includes` never
 * matched them, so no tenant ever saw a shoulder field. Near-matches stay out on
 * purpose: 'VAMTOP HC' is not 'VAM TOP', and which spellings are the same
 * connection is the tenant's call, made with the checkbox (Jason, 2026-09-28).
 */
export function isKnownShoulderedConnection(connectionType: string | null | undefined): boolean {
  return SHOULDERED_KEYS.has(String(connectionType ?? '').trim().toLowerCase());
}

export const CONNECTION_OPTIONS: { label: string; value: ConnectionType; group: string }[] = [
  // API Standard
  { group: 'API Standard', label: 'LT&C — Long Thread & Coupling', value: 'LTC' },
  { group: 'API Standard', label: 'ST&C — Short Thread & Coupling', value: 'STC' },
  { group: 'API Standard', label: 'Buttress Thread & Coupling', value: 'Buttress' },
  { group: 'API Standard', label: 'Extreme Line', value: 'Extreme Line' },
  // Premium — shouldered
  { group: 'Premium (Shouldered)', label: 'VAM TOP', value: 'VAM TOP' },
  { group: 'Premium (Shouldered)', label: 'VAM 21', value: 'VAM 21' },
  { group: 'Premium (Shouldered)', label: 'Hunting Wedge', value: 'Hunting Wedge' },
  { group: 'Premium (Shouldered)', label: 'Hunting ST-L', value: 'Hunting ST-L' },
  { group: 'Premium (Shouldered)', label: 'TenarisHydril Blue', value: 'TenarisHydril Blue' },
  { group: 'Premium (Shouldered)', label: 'TenarisHydril 513', value: 'TenarisHydril 513' },
  { group: 'Premium (Shouldered)', label: 'NOV RL-4S', value: 'NOV RL-4S' },
  { group: 'Premium (Shouldered)', label: 'NOV Wedge 521', value: 'NOV Wedge 521' },
  { group: 'Premium (Shouldered)', label: 'TMK PF', value: 'TMK PF' },
  { group: 'Premium (Shouldered)', label: 'TMK UP', value: 'TMK UP' },
  // Other
  { group: 'Other', label: 'BTC (non-standard)', value: 'BTC' },
  { group: 'Other', label: 'Other Premium', value: 'Other Premium' },
  { group: 'Other', label: 'Other', value: 'Other' },
];

// ─── API RP 5C1 Torque Table (LTC) ───────────────────────────────────────────
// Source: API RP 5C1 — Recommended Practice for Care and Use of Casing and Tubing
// Optimum torque using API modified thread compound (API 5A2/5A3)
// Min = 75% | Max = 125% per ±25% tolerance

type SpecRow = { size: string; weight: number; grade: CasingGrade; optimum: number; nm: number };

export const LTC_SPEC: SpecRow[] = [
  // 4-1/2"
  { size:'4-1/2"', weight:11.6, grade:'J-55', optimum:1620, nm:2200 },
  { size:'4-1/2"', weight:11.6, grade:'K-55', optimum:1800, nm:2440 },
  { size:'4-1/2"', weight:11.6, grade:'M-65', optimum:1880, nm:2550 },
  { size:'4-1/2"', weight:13.5, grade:'M-65', optimum:2280, nm:3090 },
  { size:'4-1/2"', weight:11.6, grade:'L-80', optimum:2230, nm:3020 },
  { size:'4-1/2"', weight:13.5, grade:'L-80', optimum:2710, nm:3670 },
  { size:'4-1/2"', weight:11.6, grade:'13Cr L-80', optimum:2230, nm:3020 },
  { size:'4-1/2"', weight:13.5, grade:'13Cr L-80', optimum:2710, nm:3670 },
  { size:'4-1/2"', weight:11.6, grade:'N-80', optimum:2280, nm:3090 },
  { size:'4-1/2"', weight:13.5, grade:'N-80', optimum:2760, nm:3740 },
  { size:'4-1/2"', weight:11.6, grade:'C-90', optimum:2450, nm:3320 },
  { size:'4-1/2"', weight:13.5, grade:'C-90', optimum:2970, nm:4030 },
  { size:'4-1/2"', weight:11.6, grade:'C-95', optimum:2580, nm:3500 },
  { size:'4-1/2"', weight:13.5, grade:'C-95', optimum:3130, nm:4240 },
  { size:'4-1/2"', weight:11.6, grade:'T-95', optimum:2580, nm:3500 },
  { size:'4-1/2"', weight:13.5, grade:'T-95', optimum:3130, nm:4240 },
  { size:'4-1/2"', weight:11.6, grade:'P-110', optimum:3020, nm:4090 },
  { size:'4-1/2"', weight:13.5, grade:'P-110', optimum:3660, nm:4960 },
  { size:'4-1/2"', weight:15.1, grade:'L-80', optimum:3250, nm:4400 },
  { size:'4-1/2"', weight:15.1, grade:'P-110', optimum:4400, nm:5970 },
  { size:'4-1/2"', weight:15.1, grade:'Q-125', optimum:4910, nm:6660 },
  // 5"
  { size:'5"', weight:13, grade:'J-55', optimum:1820, nm:2470 },
  { size:'5"', weight:15, grade:'J-55', optimum:2230, nm:3020 },
  { size:'5"', weight:13, grade:'K-55', optimum:2010, nm:2730 },
  { size:'5"', weight:15, grade:'K-55', optimum:2460, nm:3340 },
  { size:'5"', weight:13, grade:'M-65', optimum:2120, nm:2870 },
  { size:'5"', weight:15, grade:'M-65', optimum:2590, nm:3510 },
  { size:'5"', weight:18, grade:'M-65', optimum:3310, nm:4490 },
  { size:'5"', weight:21.4, grade:'M-65', optimum:4090, nm:5550 },
  { size:'5"', weight:15, grade:'L-80', optimum:3080, nm:4180 },
  { size:'5"', weight:18, grade:'L-80', optimum:3930, nm:5330 },
  { size:'5"', weight:21.4, grade:'L-80', optimum:4860, nm:6590 },
  { size:'5"', weight:23.2, grade:'L-80', optimum:5350, nm:7250 },
  { size:'5"', weight:24.1, grade:'L-80', optimum:5610, nm:7610 },
  { size:'5"', weight:15, grade:'13Cr L-80', optimum:3080, nm:4180 },
  { size:'5"', weight:18, grade:'13Cr L-80', optimum:3930, nm:5330 },
  { size:'5"', weight:21.4, grade:'13Cr L-80', optimum:4860, nm:6590 },
  { size:'5"', weight:23.2, grade:'13Cr L-80', optimum:5350, nm:7250 },
  { size:'5"', weight:24.1, grade:'13Cr L-80', optimum:5610, nm:7610 },
  { size:'5"', weight:15, grade:'N-80', optimum:3140, nm:4260 },
  { size:'5"', weight:18, grade:'N-80', optimum:4000, nm:5420 },
  { size:'5"', weight:21.4, grade:'N-80', optimum:4950, nm:6710 },
  { size:'5"', weight:23.2, grade:'N-80', optimum:5450, nm:7390 },
  { size:'5"', weight:24.1, grade:'N-80', optimum:5720, nm:7760 },
  { size:'5"', weight:15, grade:'C-90', optimum:3380, nm:4580 },
  { size:'5"', weight:18, grade:'C-90', optimum:4310, nm:5840 },
  { size:'5"', weight:21.4, grade:'C-90', optimum:5340, nm:7240 },
  { size:'5"', weight:23.2, grade:'C-90', optimum:5880, nm:7970 },
  { size:'5"', weight:24.1, grade:'C-90', optimum:6170, nm:8370 },
  { size:'5"', weight:15, grade:'C-95', optimum:3560, nm:4830 },
  { size:'5"', weight:18, grade:'C-95', optimum:4550, nm:6170 },
  { size:'5"', weight:21.4, grade:'C-95', optimum:5620, nm:7620 },
  { size:'5"', weight:23.2, grade:'C-95', optimum:6200, nm:8410 },
  { size:'5"', weight:24.1, grade:'C-95', optimum:6500, nm:8810 },
  { size:'5"', weight:15, grade:'T-95', optimum:3560, nm:4830 },
  { size:'5"', weight:18, grade:'T-95', optimum:4550, nm:6170 },
  { size:'5"', weight:21.4, grade:'T-95', optimum:5620, nm:7620 },
  { size:'5"', weight:23.2, grade:'T-95', optimum:6200, nm:8410 },
  { size:'5"', weight:24.1, grade:'T-95', optimum:6500, nm:8810 },
  { size:'5"', weight:15, grade:'P-110', optimum:4170, nm:5650 },
  { size:'5"', weight:18, grade:'P-110', optimum:5310, nm:7200 },
  { size:'5"', weight:21.4, grade:'P-110', optimum:6580, nm:8920 },
  { size:'5"', weight:23.2, grade:'P-110', optimum:7250, nm:9830 },
  { size:'5"', weight:24.1, grade:'P-110', optimum:7600, nm:10300 },
  { size:'5"', weight:18, grade:'Q-125', optimum:5930, nm:8040 },
  { size:'5"', weight:21.4, grade:'Q-125', optimum:7340, nm:9950 },
  { size:'5"', weight:23.2, grade:'Q-125', optimum:8090, nm:10970 },
  { size:'5"', weight:24.1, grade:'Q-125', optimum:8490, nm:11510 },
  // 5-1/2"
  { size:'5-1/2"', weight:15.5, grade:'J-55', optimum:2170, nm:2940 },
  { size:'5-1/2"', weight:17, grade:'J-55', optimum:2470, nm:3350 },
  { size:'5-1/2"', weight:15.5, grade:'K-55', optimum:2390, nm:3240 },
  { size:'5-1/2"', weight:17, grade:'K-55', optimum:2720, nm:3690 },
  { size:'5-1/2"', weight:15.5, grade:'M-65', optimum:2530, nm:3430 },
  { size:'5-1/2"', weight:17, grade:'M-65', optimum:2870, nm:3890 },
  { size:'5-1/2"', weight:20, grade:'M-65', optimum:3530, nm:4790 },
  { size:'5-1/2"', weight:23, grade:'M-65', optimum:4150, nm:5630 },
  { size:'5-1/2"', weight:17, grade:'L-80', optimum:3410, nm:4620 },
  { size:'5-1/2"', weight:20, grade:'L-80', optimum:4200, nm:5690 },
  { size:'5-1/2"', weight:23, grade:'L-80', optimum:4930, nm:6680 },
  { size:'5-1/2"', weight:17, grade:'13Cr L-80', optimum:3410, nm:4620 },
  { size:'5-1/2"', weight:20, grade:'13Cr L-80', optimum:4200, nm:5690 },
  { size:'5-1/2"', weight:23, grade:'13Cr L-80', optimum:4930, nm:6680 },
  { size:'5-1/2"', weight:17, grade:'N-80', optimum:3480, nm:4720 },
  { size:'5-1/2"', weight:20, grade:'N-80', optimum:4280, nm:5800 },
  { size:'5-1/2"', weight:23, grade:'N-80', optimum:5020, nm:6810 },
  { size:'5-1/2"', weight:17, grade:'C-90', optimum:3750, nm:5080 },
  { size:'5-1/2"', weight:20, grade:'C-90', optimum:4620, nm:6260 },
  { size:'5-1/2"', weight:23, grade:'C-90', optimum:5430, nm:7360 },
  { size:'5-1/2"', weight:17, grade:'C-95', optimum:3960, nm:5370 },
  { size:'5-1/2"', weight:20, grade:'C-95', optimum:4870, nm:6600 },
  { size:'5-1/2"', weight:23, grade:'C-95', optimum:5720, nm:7760 },
  { size:'5-1/2"', weight:17, grade:'T-95', optimum:3960, nm:5370 },
  { size:'5-1/2"', weight:20, grade:'T-95', optimum:4870, nm:6600 },
  { size:'5-1/2"', weight:23, grade:'T-95', optimum:5720, nm:7760 },
  { size:'5-1/2"', weight:17, grade:'P-110', optimum:4620, nm:6260 },
  { size:'5-1/2"', weight:20, grade:'P-110', optimum:5690, nm:7710 },
  { size:'5-1/2"', weight:23, grade:'P-110', optimum:6680, nm:9060 },
  { size:'5-1/2"', weight:23, grade:'Q-125', optimum:7470, nm:10130 },
  // 6-5/8"
  { size:'6-5/8"', weight:20, grade:'J-55', optimum:2660, nm:3610 },
  { size:'6-5/8"', weight:24, grade:'J-55', optimum:3400, nm:4610 },
  { size:'6-5/8"', weight:20, grade:'K-55', optimum:2900, nm:3930 },
  { size:'6-5/8"', weight:24, grade:'K-55', optimum:3720, nm:5040 },
  { size:'6-5/8"', weight:20, grade:'M-65', optimum:3090, nm:4190 },
  { size:'6-5/8"', weight:24, grade:'M-65', optimum:3960, nm:5370 },
  { size:'6-5/8"', weight:28, grade:'M-65', optimum:4830, nm:6550 },
  { size:'6-5/8"', weight:24, grade:'L-80', optimum:4730, nm:6410 },
  { size:'6-5/8"', weight:28, grade:'L-80', optimum:5760, nm:7810 },
  { size:'6-5/8"', weight:32, grade:'L-80', optimum:6660, nm:9030 },
  { size:'6-5/8"', weight:24, grade:'13Cr L-80', optimum:4730, nm:6410 },
  { size:'6-5/8"', weight:28, grade:'13Cr L-80', optimum:5760, nm:7810 },
  { size:'6-5/8"', weight:32, grade:'13Cr L-80', optimum:6660, nm:9030 },
  { size:'6-5/8"', weight:24, grade:'N-80', optimum:4810, nm:6520 },
  { size:'6-5/8"', weight:28, grade:'N-80', optimum:5860, nm:7950 },
  { size:'6-5/8"', weight:32, grade:'N-80', optimum:6780, nm:9190 },
  { size:'6-5/8"', weight:24, grade:'C-90', optimum:5210, nm:7060 },
  { size:'6-5/8"', weight:28, grade:'C-90', optimum:6350, nm:8610 },
  { size:'6-5/8"', weight:32, grade:'C-90', optimum:7340, nm:9950 },
  { size:'6-5/8"', weight:24, grade:'C-95', optimum:5490, nm:7440 },
  { size:'6-5/8"', weight:28, grade:'C-95', optimum:6690, nm:9070 },
  { size:'6-5/8"', weight:32, grade:'C-95', optimum:7740, nm:10490 },
  { size:'6-5/8"', weight:24, grade:'T-95', optimum:5490, nm:7440 },
  { size:'6-5/8"', weight:28, grade:'T-95', optimum:6690, nm:9070 },
  { size:'6-5/8"', weight:32, grade:'T-95', optimum:7740, nm:10490 },
  { size:'6-5/8"', weight:24, grade:'P-110', optimum:6410, nm:8690 },
  { size:'6-5/8"', weight:28, grade:'P-110', optimum:7810, nm:10590 },
  { size:'6-5/8"', weight:32, grade:'P-110', optimum:9040, nm:12260 },
  { size:'6-5/8"', weight:32, grade:'Q-125', optimum:10110, nm:13710 },
  // 7"
  { size:'7"', weight:23, grade:'J-55', optimum:3130, nm:4240 },
  { size:'7"', weight:26, grade:'J-55', optimum:3670, nm:4980 },
  { size:'7"', weight:23, grade:'K-55', optimum:3410, nm:4620 },
  { size:'7"', weight:26, grade:'K-55', optimum:4010, nm:5440 },
  { size:'7"', weight:23, grade:'M-65', optimum:3640, nm:4940 },
  { size:'7"', weight:26, grade:'M-65', optimum:4280, nm:5800 },
  { size:'7"', weight:29, grade:'M-65', optimum:4920, nm:6670 },
  { size:'7"', weight:32, grade:'M-65', optimum:5540, nm:7510 },
  { size:'7"', weight:23, grade:'L-80', optimum:4350, nm:5900 },
  { size:'7"', weight:26, grade:'L-80', optimum:5110, nm:6930 },
  { size:'7"', weight:29, grade:'L-80', optimum:5870, nm:7960 },
  { size:'7"', weight:32, grade:'L-80', optimum:6610, nm:8960 },
  { size:'7"', weight:35, grade:'L-80', optimum:7340, nm:9950 },
  { size:'7"', weight:38, grade:'L-80', optimum:8010, nm:10860 },
  { size:'7"', weight:23, grade:'13Cr L-80', optimum:4350, nm:5900 },
  { size:'7"', weight:26, grade:'13Cr L-80', optimum:5110, nm:6930 },
  { size:'7"', weight:29, grade:'13Cr L-80', optimum:5870, nm:7960 },
  { size:'7"', weight:32, grade:'13Cr L-80', optimum:6610, nm:8960 },
  { size:'7"', weight:35, grade:'13Cr L-80', optimum:7340, nm:9950 },
  { size:'7"', weight:38, grade:'13Cr L-80', optimum:8010, nm:10860 },
  { size:'7"', weight:23, grade:'N-80', optimum:4420, nm:5990 },
  { size:'7"', weight:26, grade:'N-80', optimum:5190, nm:7040 },
  { size:'7"', weight:29, grade:'N-80', optimum:5970, nm:8090 },
  { size:'7"', weight:32, grade:'N-80', optimum:6720, nm:9110 },
  { size:'7"', weight:35, grade:'N-80', optimum:7460, nm:10110 },
  { size:'7"', weight:38, grade:'N-80', optimum:8140, nm:11040 },
  { size:'7"', weight:23, grade:'C-90', optimum:4790, nm:6490 },
  { size:'7"', weight:26, grade:'C-90', optimum:5630, nm:7630 },
  { size:'7"', weight:29, grade:'C-90', optimum:6480, nm:8790 },
  { size:'7"', weight:32, grade:'C-90', optimum:7290, nm:9880 },
  { size:'7"', weight:35, grade:'C-90', optimum:8090, nm:10970 },
  { size:'7"', weight:38, grade:'C-90', optimum:8830, nm:11970 },
  { size:'7"', weight:23, grade:'C-95', optimum:5050, nm:6850 },
  { size:'7"', weight:26, grade:'C-95', optimum:5930, nm:8040 },
  { size:'7"', weight:29, grade:'C-95', optimum:6830, nm:9260 },
  { size:'7"', weight:32, grade:'C-95', optimum:7680, nm:10410 },
  { size:'7"', weight:35, grade:'C-95', optimum:8530, nm:11570 },
  { size:'7"', weight:38, grade:'C-95', optimum:9310, nm:12620 },
  { size:'7"', weight:23, grade:'T-95', optimum:5050, nm:6850 },
  { size:'7"', weight:26, grade:'T-95', optimum:5930, nm:8040 },
  { size:'7"', weight:29, grade:'T-95', optimum:6830, nm:9260 },
  { size:'7"', weight:32, grade:'T-95', optimum:7680, nm:10410 },
  { size:'7"', weight:35, grade:'T-95', optimum:8530, nm:11570 },
  { size:'7"', weight:38, grade:'T-95', optimum:9310, nm:12620 },
  { size:'7"', weight:26, grade:'P-110', optimum:6930, nm:9400 },
  { size:'7"', weight:29, grade:'P-110', optimum:7970, nm:10810 },
  { size:'7"', weight:32, grade:'P-110', optimum:8970, nm:12160 },
  { size:'7"', weight:35, grade:'P-110', optimum:9960, nm:13500 },
  { size:'7"', weight:38, grade:'P-110', optimum:10870, nm:14740 },
  { size:'7"', weight:35, grade:'Q-125', optimum:11150, nm:15120 },
  { size:'7"', weight:38, grade:'Q-125', optimum:12160, nm:16490 },
  // 7-5/8"
  { size:'7-5/8"', weight:26.4, grade:'J-55', optimum:3460, nm:4690 },
  { size:'7-5/8"', weight:26.4, grade:'K-55', optimum:3770, nm:5110 },
  { size:'7-5/8"', weight:26.4, grade:'M-65', optimum:4040, nm:5480 },
  { size:'7-5/8"', weight:29.7, grade:'M-65', optimum:4740, nm:6430 },
  { size:'7-5/8"', weight:33.7, grade:'M-65', optimum:5560, nm:7540 },
  { size:'7-5/8"', weight:26.4, grade:'L-80', optimum:4820, nm:6540 },
  { size:'7-5/8"', weight:29.7, grade:'L-80', optimum:5670, nm:7690 },
  { size:'7-5/8"', weight:33.7, grade:'L-80', optimum:6640, nm:9000 },
  { size:'7-5/8"', weight:39, grade:'L-80', optimum:7860, nm:10660 },
  { size:'7-5/8"', weight:42.8, grade:'L-80', optimum:8910, nm:12080 },
  { size:'7-5/8"', weight:45.3, grade:'L-80', optimum:9470, nm:12840 },
  { size:'7-5/8"', weight:47.1, grade:'L-80', optimum:9970, nm:13520 },
  { size:'7-5/8"', weight:26.4, grade:'13Cr L-80', optimum:4820, nm:6540 },
  { size:'7-5/8"', weight:29.7, grade:'13Cr L-80', optimum:5670, nm:7690 },
  { size:'7-5/8"', weight:33.7, grade:'13Cr L-80', optimum:6640, nm:9000 },
  { size:'7-5/8"', weight:39, grade:'13Cr L-80', optimum:7860, nm:10660 },
  { size:'7-5/8"', weight:42.8, grade:'13Cr L-80', optimum:8910, nm:12080 },
  { size:'7-5/8"', weight:45.3, grade:'13Cr L-80', optimum:9470, nm:12840 },
  { size:'7-5/8"', weight:47.1, grade:'13Cr L-80', optimum:9970, nm:13520 },
  { size:'7-5/8"', weight:26.4, grade:'N-80', optimum:4900, nm:6640 },
  { size:'7-5/8"', weight:29.7, grade:'N-80', optimum:5750, nm:7800 },
  { size:'7-5/8"', weight:33.7, grade:'N-80', optimum:6740, nm:9140 },
  { size:'7-5/8"', weight:39, grade:'N-80', optimum:7980, nm:10820 },
  { size:'7-5/8"', weight:42.8, grade:'N-80', optimum:9060, nm:12280 },
  { size:'7-5/8"', weight:45.3, grade:'N-80', optimum:9620, nm:13040 },
  { size:'7-5/8"', weight:47.1, grade:'N-80', optimum:10130, nm:13730 },
  { size:'7-5/8"', weight:26.4, grade:'C-90', optimum:5320, nm:7210 },
  { size:'7-5/8"', weight:29.7, grade:'C-90', optimum:6250, nm:8470 },
  { size:'7-5/8"', weight:33.7, grade:'C-90', optimum:7330, nm:9940 },
  { size:'7-5/8"', weight:39, grade:'C-90', optimum:8670, nm:11750 },
  { size:'7-5/8"', weight:42.8, grade:'C-90', optimum:9840, nm:13340 },
  { size:'7-5/8"', weight:45.3, grade:'C-90', optimum:10450, nm:14170 },
  { size:'7-5/8"', weight:47.1, grade:'C-90', optimum:11000, nm:14910 },
  { size:'7-5/8"', weight:26.4, grade:'C-95', optimum:5600, nm:7590 },
  { size:'7-5/8"', weight:29.7, grade:'C-95', optimum:6590, nm:8930 },
  { size:'7-5/8"', weight:33.7, grade:'C-95', optimum:7720, nm:10470 },
  { size:'7-5/8"', weight:39, grade:'C-95', optimum:9140, nm:12390 },
  { size:'7-5/8"', weight:42.8, grade:'C-95', optimum:10370, nm:14060 },
  { size:'7-5/8"', weight:45.3, grade:'C-95', optimum:11010, nm:14930 },
  { size:'7-5/8"', weight:47.1, grade:'C-95', optimum:11590, nm:15710 },
  { size:'7-5/8"', weight:26.4, grade:'T-95', optimum:5600, nm:7590 },
  { size:'7-5/8"', weight:29.7, grade:'T-95', optimum:6590, nm:8930 },
  { size:'7-5/8"', weight:33.7, grade:'T-95', optimum:7720, nm:10470 },
  { size:'7-5/8"', weight:39, grade:'T-95', optimum:9140, nm:12390 },
  { size:'7-5/8"', weight:42.8, grade:'T-95', optimum:10370, nm:14060 },
  { size:'7-5/8"', weight:45.3, grade:'T-95', optimum:11010, nm:14930 },
  { size:'7-5/8"', weight:47.1, grade:'T-95', optimum:11590, nm:15710 },
  { size:'7-5/8"', weight:29.7, grade:'P-110', optimum:7690, nm:10430 },
  { size:'7-5/8"', weight:33.7, grade:'P-110', optimum:9010, nm:12220 },
  { size:'7-5/8"', weight:39, grade:'P-110', optimum:10660, nm:14450 },
  { size:'7-5/8"', weight:42.8, grade:'P-110', optimum:12100, nm:16410 },
  { size:'7-5/8"', weight:45.3, grade:'P-110', optimum:12850, nm:17420 },
  { size:'7-5/8"', weight:47.1, grade:'P-110', optimum:13530, nm:18340 },
  { size:'7-5/8"', weight:39, grade:'Q-125', optimum:11940, nm:16190 },
  { size:'7-5/8"', weight:42.8, grade:'Q-125', optimum:13550, nm:18370 },
  { size:'7-5/8"', weight:45.3, grade:'Q-125', optimum:14390, nm:19510 },
  { size:'7-5/8"', weight:47.1, grade:'Q-125', optimum:15150, nm:20540 },
  // 8-5/8"
  { size:'8-5/8"', weight:32, grade:'J-55', optimum:4170, nm:5650 },
  { size:'8-5/8"', weight:36, grade:'J-55', optimum:4860, nm:6590 },
  { size:'8-5/8"', weight:32, grade:'K-55', optimum:4520, nm:6130 },
  { size:'8-5/8"', weight:36, grade:'K-55', optimum:5260, nm:7130 },
  { size:'8-5/8"', weight:32, grade:'M-65', optimum:4870, nm:6600 },
  { size:'8-5/8"', weight:36, grade:'M-65', optimum:5670, nm:7690 },
  { size:'8-5/8"', weight:40, grade:'M-65', optimum:6490, nm:8800 },
  { size:'8-5/8"', weight:36, grade:'L-80', optimum:6780, nm:9190 },
  { size:'8-5/8"', weight:40, grade:'L-80', optimum:7760, nm:10520 },
  { size:'8-5/8"', weight:44, grade:'L-80', optimum:8740, nm:11850 },
  { size:'8-5/8"', weight:49, grade:'L-80', optimum:9830, nm:13330 },
  { size:'8-5/8"', weight:36, grade:'13Cr L-80', optimum:6780, nm:9190 },
  { size:'8-5/8"', weight:40, grade:'13Cr L-80', optimum:7760, nm:10520 },
  { size:'8-5/8"', weight:44, grade:'13Cr L-80', optimum:8740, nm:11850 },
  { size:'8-5/8"', weight:49, grade:'13Cr L-80', optimum:9830, nm:13330 },
  { size:'8-5/8"', weight:36, grade:'N-80', optimum:6880, nm:9330 },
  { size:'8-5/8"', weight:40, grade:'N-80', optimum:7880, nm:10680 },
  { size:'8-5/8"', weight:44, grade:'N-80', optimum:8870, nm:12030 },
  { size:'8-5/8"', weight:49, grade:'N-80', optimum:9970, nm:13520 },
  { size:'8-5/8"', weight:36, grade:'C-90', optimum:7490, nm:10160 },
  { size:'8-5/8"', weight:40, grade:'C-90', optimum:8580, nm:11630 },
  { size:'8-5/8"', weight:44, grade:'C-90', optimum:9650, nm:13080 },
  { size:'8-5/8"', weight:49, grade:'C-90', optimum:10850, nm:14710 },
  { size:'8-5/8"', weight:36, grade:'C-95', optimum:7890, nm:10700 },
  { size:'8-5/8"', weight:40, grade:'C-95', optimum:9040, nm:12260 },
  { size:'8-5/8"', weight:44, grade:'C-95', optimum:10170, nm:13790 },
  { size:'8-5/8"', weight:49, grade:'C-95', optimum:11440, nm:15510 },
  { size:'8-5/8"', weight:36, grade:'T-95', optimum:7890, nm:10700 },
  { size:'8-5/8"', weight:40, grade:'T-95', optimum:9040, nm:12260 },
  { size:'8-5/8"', weight:44, grade:'T-95', optimum:10170, nm:13790 },
  { size:'8-5/8"', weight:49, grade:'T-95', optimum:11440, nm:15510 },
  { size:'8-5/8"', weight:40, grade:'P-110', optimum:10550, nm:14300 },
  { size:'8-5/8"', weight:44, grade:'P-110', optimum:11860, nm:16080 },
  { size:'8-5/8"', weight:49, grade:'P-110', optimum:13350, nm:18100 },
  { size:'8-5/8"', weight:49, grade:'Q-125', optimum:14960, nm:20280 },
  // 9-5/8"
  { size:'9-5/8"', weight:36, grade:'J-55', optimum:4530, nm:6140 },
  { size:'9-5/8"', weight:40, grade:'J-55', optimum:5200, nm:7050 },
  { size:'9-5/8"', weight:36, grade:'K-55', optimum:4890, nm:6630 },
  { size:'9-5/8"', weight:40, grade:'K-55', optimum:5610, nm:7610 },
  { size:'9-5/8"', weight:36, grade:'M-65', optimum:5290, nm:7170 },
  { size:'9-5/8"', weight:40, grade:'M-65', optimum:6070, nm:8230 },
  { size:'9-5/8"', weight:43.5, grade:'M-65', optimum:6790, nm:9210 },
  { size:'9-5/8"', weight:47, grade:'M-65', optimum:7450, nm:10100 },
  { size:'9-5/8"', weight:40, grade:'L-80', optimum:7270, nm:9860 },
  { size:'9-5/8"', weight:43.5, grade:'L-80', optimum:8130, nm:11020 },
  { size:'9-5/8"', weight:47, grade:'L-80', optimum:8930, nm:12110 },
  { size:'9-5/8"', weight:53.5, grade:'L-80', optimum:10470, nm:14200 },
  { size:'9-5/8"', weight:58.4, grade:'L-80', optimum:11510, nm:15610 },
  { size:'9-5/8"', weight:40, grade:'13Cr L-80', optimum:7270, nm:9860 },
  { size:'9-5/8"', weight:43.5, grade:'13Cr L-80', optimum:8130, nm:11020 },
  { size:'9-5/8"', weight:47, grade:'13Cr L-80', optimum:8930, nm:12110 },
  { size:'9-5/8"', weight:53.5, grade:'13Cr L-80', optimum:10470, nm:14200 },
  { size:'9-5/8"', weight:58.4, grade:'13Cr L-80', optimum:11510, nm:15610 },
  { size:'9-5/8"', weight:40, grade:'N-80', optimum:7370, nm:9990 },
  { size:'9-5/8"', weight:43.5, grade:'N-80', optimum:8250, nm:11190 },
  { size:'9-5/8"', weight:47, grade:'N-80', optimum:9050, nm:12270 },
  { size:'9-5/8"', weight:53.5, grade:'N-80', optimum:10620, nm:14400 },
  { size:'9-5/8"', weight:58.4, grade:'N-80', optimum:11670, nm:15820 },
  { size:'9-5/8"', weight:40, grade:'C-90', optimum:8040, nm:10900 },
  { size:'9-5/8"', weight:43.5, grade:'C-90', optimum:8990, nm:12190 },
  { size:'9-5/8"', weight:47, grade:'C-90', optimum:9870, nm:13380 },
  { size:'9-5/8"', weight:53.5, grade:'C-90', optimum:11570, nm:15690 },
  { size:'9-5/8"', weight:58.4, grade:'C-90', optimum:12720, nm:17250 },
  { size:'9-5/8"', weight:40, grade:'C-95', optimum:8470, nm:11480 },
  { size:'9-5/8"', weight:43.5, grade:'C-95', optimum:9480, nm:12850 },
  { size:'9-5/8"', weight:47, grade:'C-95', optimum:10400, nm:14100 },
  { size:'9-5/8"', weight:53.5, grade:'C-95', optimum:12200, nm:16540 },
  { size:'9-5/8"', weight:58.4, grade:'C-95', optimum:13410, nm:18180 },
  { size:'9-5/8"', weight:40, grade:'T-95', optimum:8470, nm:11480 },
  { size:'9-5/8"', weight:43.5, grade:'T-95', optimum:9480, nm:12850 },
  { size:'9-5/8"', weight:47, grade:'T-95', optimum:10400, nm:14100 },
  { size:'9-5/8"', weight:53.5, grade:'T-95', optimum:12200, nm:16540 },
  { size:'9-5/8"', weight:58.4, grade:'T-95', optimum:13410, nm:18180 },
  { size:'9-5/8"', weight:43.5, grade:'P-110', optimum:11050, nm:14980 },
  { size:'9-5/8"', weight:47, grade:'P-110', optimum:12130, nm:16450 },
  { size:'9-5/8"', weight:53.5, grade:'P-110', optimum:14220, nm:19280 },
  { size:'9-5/8"', weight:58.4, grade:'P-110', optimum:15630, nm:21190 },
  { size:'9-5/8"', weight:47, grade:'Q-125', optimum:13600, nm:18440 },
  { size:'9-5/8"', weight:53.5, grade:'Q-125', optimum:15950, nm:21630 },
  { size:'9-5/8"', weight:58.4, grade:'Q-125', optimum:17540, nm:23780 },
  // 20" — minimum torque only (position-based makeup)
  { size:'20"', weight:94, grade:'H-40', optimum:6730, nm:9120 },
  { size:'20"', weight:94, grade:'J-55', optimum:9070, nm:12300 },
  { size:'20"', weight:106.5, grade:'J-55', optimum:10560, nm:14320 },
  { size:'20"', weight:133, grade:'J-55', optimum:13790, nm:18700 },
  { size:'20"', weight:94, grade:'K-55', optimum:9550, nm:12950 },
  { size:'20"', weight:106.5, grade:'K-55', optimum:11130, nm:15090 },
  { size:'20"', weight:133, grade:'K-55', optimum:14530, nm:19700 },
  { size:'20"', weight:94, grade:'M-65', optimum:10630, nm:14410 },
  { size:'20"', weight:106.5, grade:'M-65', optimum:12380, nm:16790 },
];

// STC spec — API RP 5C1 Table values
export const STC_SPEC: SpecRow[] = [
  // 4-1/2"
  { size:'4-1/2"', weight:9.5, grade:'H-40', optimum:770, nm:1040 },
  { size:'4-1/2"', weight:9.5, grade:'J-55', optimum:1010, nm:1370 },
  { size:'4-1/2"', weight:10.5, grade:'J-55', optimum:1320, nm:1790 },
  { size:'4-1/2"', weight:11.6, grade:'J-55', optimum:1540, nm:2090 },
  { size:'4-1/2"', weight:9.5, grade:'K-55', optimum:1120, nm:1520 },
  { size:'4-1/2"', weight:10.5, grade:'K-55', optimum:1460, nm:1980 },
  { size:'4-1/2"', weight:11.6, grade:'K-55', optimum:1700, nm:2300 },
  { size:'4-1/2"', weight:9.5, grade:'M-65', optimum:1180, nm:1600 },
  { size:'4-1/2"', weight:10.5, grade:'M-65', optimum:1540, nm:2090 },
  // 5"
  { size:'5"', weight:11.5, grade:'J-55', optimum:1330, nm:1800 },
  { size:'5"', weight:13, grade:'J-55', optimum:1690, nm:2290 },
  { size:'5"', weight:15, grade:'J-55', optimum:2070, nm:2810 },
  { size:'5"', weight:11.5, grade:'K-55', optimum:1470, nm:1990 },
  { size:'5"', weight:13, grade:'K-55', optimum:1860, nm:2520 },
  { size:'5"', weight:15, grade:'K-55', optimum:2280, nm:3090 },
  { size:'5"', weight:11.5, grade:'M-65', optimum:1550, nm:2100 },
  { size:'5"', weight:13, grade:'M-65', optimum:1960, nm:2660 },
  // 5-1/2"
  { size:'5-1/2"', weight:14, grade:'H-40', optimum:1300, nm:1760 },
  { size:'5-1/2"', weight:14, grade:'J-55', optimum:1720, nm:2330 },
  { size:'5-1/2"', weight:15.5, grade:'J-55', optimum:2020, nm:2740 },
  { size:'5-1/2"', weight:17, grade:'J-55', optimum:2290, nm:3100 },
  { size:'5-1/2"', weight:14, grade:'K-55', optimum:1890, nm:2560 },
  { size:'5-1/2"', weight:15.5, grade:'K-55', optimum:2220, nm:3010 },
  { size:'5-1/2"', weight:17, grade:'K-55', optimum:2520, nm:3420 },
  { size:'5-1/2"', weight:14, grade:'M-65', optimum:2000, nm:2710 },
  { size:'5-1/2"', weight:15.5, grade:'M-65', optimum:2350, nm:3190 },
  // 6-5/8"
  { size:'6-5/8"', weight:20, grade:'H-40', optimum:1840, nm:2490 },
  { size:'6-5/8"', weight:20, grade:'J-55', optimum:2450, nm:3320 },
  { size:'6-5/8"', weight:24, grade:'J-55', optimum:3140, nm:4260 },
  { size:'6-5/8"', weight:20, grade:'K-55', optimum:2670, nm:3620 },
  { size:'6-5/8"', weight:24, grade:'K-55', optimum:3420, nm:4640 },
  { size:'6-5/8"', weight:20, grade:'M-65', optimum:2850, nm:3860 },
  // 7"
  { size:'7"', weight:17, grade:'H-40', optimum:1220, nm:1650 },
  { size:'7"', weight:20, grade:'H-40', optimum:1760, nm:2390 },
  { size:'7"', weight:20, grade:'J-55', optimum:2340, nm:3170 },
  { size:'7"', weight:23, grade:'J-55', optimum:2840, nm:3850 },
  { size:'7"', weight:26, grade:'J-55', optimum:3340, nm:4530 },
  { size:'7"', weight:20, grade:'K-55', optimum:2540, nm:3440 },
  { size:'7"', weight:23, grade:'K-55', optimum:3090, nm:4190 },
  { size:'7"', weight:26, grade:'K-55', optimum:3640, nm:4940 },
  { size:'7"', weight:20, grade:'M-65', optimum:2730, nm:3700 },
  // 7-5/8"
  { size:'7-5/8"', weight:24, grade:'H-40', optimum:2120, nm:2870 },
  { size:'7-5/8"', weight:26.4, grade:'J-55', optimum:3150, nm:4270 },
  { size:'7-5/8"', weight:26.4, grade:'K-55', optimum:3420, nm:4640 },
  { size:'7-5/8"', weight:26.4, grade:'M-65', optimum:3680, nm:4990 },
  // 8-5/8"
  { size:'8-5/8"', weight:28, grade:'H-40', optimum:2330, nm:3160 },
  { size:'8-5/8"', weight:32, grade:'H-40', optimum:2790, nm:3780 },
  { size:'8-5/8"', weight:24, grade:'J-55', optimum:2440, nm:3310 },
  { size:'8-5/8"', weight:32, grade:'J-55', optimum:3720, nm:5040 },
  { size:'8-5/8"', weight:36, grade:'J-55', optimum:4340, nm:5880 },
  { size:'8-5/8"', weight:24, grade:'K-55', optimum:2630, nm:3570 },
  { size:'8-5/8"', weight:32, grade:'K-55', optimum:4020, nm:5450 },
  { size:'8-5/8"', weight:36, grade:'K-55', optimum:4680, nm:6350 },
  { size:'8-5/8"', weight:24, grade:'M-65', optimum:2850, nm:3860 },
  { size:'8-5/8"', weight:28, grade:'M-65', optimum:3620, nm:4910 },
  { size:'8-5/8"', weight:32, grade:'M-65', optimum:4350, nm:5900 },
  { size:'8-5/8"', weight:36, grade:'M-65', optimum:5060, nm:6860 },
  // 9-5/8"
  { size:'9-5/8"', weight:32.3, grade:'H-40', optimum:2540, nm:3440 },
  { size:'9-5/8"', weight:36, grade:'H-40', optimum:2940, nm:3990 },
  { size:'9-5/8"', weight:36, grade:'J-55', optimum:3940, nm:5340 },
  { size:'9-5/8"', weight:40, grade:'J-55', optimum:4520, nm:6130 },
  { size:'9-5/8"', weight:36, grade:'K-55', optimum:4230, nm:5740 },
  { size:'9-5/8"', weight:40, grade:'K-55', optimum:4860, nm:6590 },
  { size:'9-5/8"', weight:36, grade:'M-65', optimum:4600, nm:6240 },
  { size:'9-5/8"', weight:40, grade:'M-65', optimum:5280, nm:7160 },
  // 10-3/4"
  { size:'10-3/4"', weight:32.75, grade:'H-40', optimum:2050, nm:2780 },
  { size:'10-3/4"', weight:40.5, grade:'H-40', optimum:3140, nm:4260 },
  { size:'10-3/4"', weight:40.5, grade:'J-55', optimum:4200, nm:5690 },
  { size:'10-3/4"', weight:45.5, grade:'J-55', optimum:4930, nm:6680 },
  { size:'10-3/4"', weight:51, grade:'J-55', optimum:5650, nm:7660 },
  { size:'10-3/4"', weight:40.5, grade:'K-55', optimum:4500, nm:6100 },
  { size:'10-3/4"', weight:45.5, grade:'K-55', optimum:5280, nm:7160 },
  { size:'10-3/4"', weight:51, grade:'K-55', optimum:6060, nm:8220 },
  { size:'10-3/4"', weight:40.5, grade:'M-65', optimum:4910, nm:6660 },
  { size:'10-3/4"', weight:45.5, grade:'M-65', optimum:5760, nm:7810 },
  { size:'10-3/4"', weight:51, grade:'M-65', optimum:6610, nm:8960 },
  { size:'10-3/4"', weight:55.5, grade:'M-65', optimum:6610, nm:8960 },
  { size:'10-3/4"', weight:51, grade:'L-80', optimum:7940, nm:10770 },
  { size:'10-3/4"', weight:55.5, grade:'L-80', optimum:8840, nm:11990 },
  { size:'10-3/4"', weight:51, grade:'13Cr L-80', optimum:7940, nm:10770 },
  { size:'10-3/4"', weight:55.5, grade:'13Cr L-80', optimum:8840, nm:11990 },
  { size:'10-3/4"', weight:51, grade:'N-80', optimum:8040, nm:10900 },
  { size:'10-3/4"', weight:55.5, grade:'N-80', optimum:8950, nm:12130 },
  { size:'10-3/4"', weight:51, grade:'C-90', optimum:8790, nm:11920 },
  { size:'10-3/4"', weight:55.5, grade:'C-90', optimum:9790, nm:13270 },
  { size:'10-3/4"', weight:60.7, grade:'C-90', optimum:10890, nm:14760 },
  { size:'10-3/4"', weight:65.7, grade:'C-90', optimum:11980, nm:16240 },
  { size:'10-3/4"', weight:51, grade:'C-95', optimum:9270, nm:12570 },
  { size:'10-3/4"', weight:55.5, grade:'C-95', optimum:10320, nm:13990 },
  { size:'10-3/4"', weight:51, grade:'T-95', optimum:9270, nm:12570 },
  { size:'10-3/4"', weight:55.5, grade:'T-95', optimum:10320, nm:13990 },
  { size:'10-3/4"', weight:60.7, grade:'T-95', optimum:11480, nm:15560 },
  { size:'10-3/4"', weight:65.7, grade:'T-95', optimum:12630, nm:17120 },
  { size:'10-3/4"', weight:51, grade:'P-110', optimum:10790, nm:14630 },
  { size:'10-3/4"', weight:55.5, grade:'P-110', optimum:12020, nm:16300 },
  { size:'10-3/4"', weight:60.7, grade:'P-110', optimum:13370, nm:18130 },
  { size:'10-3/4"', weight:65.7, grade:'P-110', optimum:14710, nm:19940 },
  { size:'10-3/4"', weight:60.7, grade:'Q-125', optimum:15020, nm:20360 },
  { size:'10-3/4"', weight:65.7, grade:'Q-125', optimum:16520, nm:22400 },
  // 11-3/4"
  { size:'11-3/4"', weight:42, grade:'H-40', optimum:3070, nm:4160 },
  { size:'11-3/4"', weight:47, grade:'J-55', optimum:4770, nm:6470 },
  { size:'11-3/4"', weight:54, grade:'J-55', optimum:5680, nm:7700 },
  { size:'11-3/4"', weight:60, grade:'J-55', optimum:6490, nm:8800 },
  { size:'11-3/4"', weight:47, grade:'K-55', optimum:5090, nm:6900 },
  { size:'11-3/4"', weight:54, grade:'K-55', optimum:6060, nm:8220 },
  { size:'11-3/4"', weight:60, grade:'K-55', optimum:6930, nm:9400 },
  { size:'11-3/4"', weight:47, grade:'M-65', optimum:5570, nm:7550 },
  { size:'11-3/4"', weight:54, grade:'M-65', optimum:6640, nm:9000 },
  { size:'11-3/4"', weight:60, grade:'M-65', optimum:7590, nm:10290 },
  { size:'11-3/4"', weight:60, grade:'L-80', optimum:9130, nm:12380 },
  { size:'11-3/4"', weight:60, grade:'13Cr L-80', optimum:9130, nm:12380 },
  { size:'11-3/4"', weight:60, grade:'N-80', optimum:9240, nm:12530 },
  { size:'11-3/4"', weight:60, grade:'C-90', optimum:10110, nm:13710 },
  { size:'11-3/4"', weight:60, grade:'C-95', optimum:10660, nm:14450 },
  { size:'11-3/4"', weight:60, grade:'T-95', optimum:10660, nm:14450 },
  { size:'11-3/4"', weight:60, grade:'P-110', optimum:12420, nm:16840 },
  { size:'11-3/4"', weight:60, grade:'Q-125', optimum:13950, nm:18910 },
  // 13-3/8"
  { size:'13-3/8"', weight:48, grade:'H-40', optimum:3220, nm:4370 },
  { size:'13-3/8"', weight:54.5, grade:'J-55', optimum:5140, nm:6970 },
  { size:'13-3/8"', weight:61, grade:'J-55', optimum:5950, nm:8070 },
  { size:'13-3/8"', weight:68, grade:'J-55', optimum:6750, nm:9150 },
  { size:'13-3/8"', weight:54.5, grade:'K-55', optimum:5470, nm:7420 },
  { size:'13-3/8"', weight:61, grade:'K-55', optimum:6330, nm:8580 },
  { size:'13-3/8"', weight:68, grade:'K-55', optimum:7180, nm:9730 },
  { size:'13-3/8"', weight:54.5, grade:'M-65', optimum:6020, nm:8160 },
  { size:'13-3/8"', weight:61, grade:'M-65', optimum:6970, nm:9450 },
  { size:'13-3/8"', weight:68, grade:'M-65', optimum:7910, nm:10720 },
  { size:'13-3/8"', weight:68, grade:'L-80', optimum:9520, nm:12910 },
  { size:'13-3/8"', weight:72, grade:'L-80', optimum:10290, nm:13950 },
  { size:'13-3/8"', weight:68, grade:'13Cr L-80', optimum:9520, nm:12910 },
  { size:'13-3/8"', weight:72, grade:'13Cr L-80', optimum:10290, nm:13950 },
  { size:'13-3/8"', weight:68, grade:'N-80', optimum:9630, nm:13060 },
  { size:'13-3/8"', weight:72, grade:'N-80', optimum:10400, nm:14100 },
  { size:'13-3/8"', weight:68, grade:'C-90', optimum:10570, nm:14330 },
  { size:'13-3/8"', weight:72, grade:'C-90', optimum:11420, nm:15480 },
  { size:'13-3/8"', weight:68, grade:'C-95', optimum:11140, nm:15100 },
  { size:'13-3/8"', weight:72, grade:'C-95', optimum:12040, nm:16320 },
  { size:'13-3/8"', weight:68, grade:'T-95', optimum:11140, nm:15100 },
  { size:'13-3/8"', weight:72, grade:'T-95', optimum:12040, nm:16320 },
  { size:'13-3/8"', weight:68, grade:'P-110', optimum:12970, nm:17580 },
  { size:'13-3/8"', weight:72, grade:'P-110', optimum:14010, nm:19000 },
  { size:'13-3/8"', weight:72, grade:'Q-125', optimum:15760, nm:21370 },
  // 16" — minimum torque only (position-based makeup)
  { size:'16"', weight:65, grade:'H-40', optimum:4390, nm:5950 },
  { size:'16"', weight:75, grade:'J-55', optimum:7100, nm:9630 },
  { size:'16"', weight:84, grade:'J-55', optimum:8170, nm:11080 },
  { size:'16"', weight:75, grade:'K-55', optimum:7520, nm:10200 },
  { size:'16"', weight:84, grade:'K-55', optimum:8650, nm:11730 },
  { size:'16"', weight:75, grade:'M-65', optimum:8320, nm:11280 },
  { size:'16"', weight:84, grade:'M-65', optimum:9570, nm:12980 },
  // 18-5/8" — minimum torque only (position-based makeup)
  { size:'18-5/8"', weight:87.5, grade:'H-40', optimum:5590, nm:7580 },
  { size:'18-5/8"', weight:87.5, grade:'J-55', optimum:7540, nm:10220 },
  { size:'18-5/8"', weight:87.5, grade:'K-55', optimum:7940, nm:10770 },
  { size:'18-5/8"', weight:87.5, grade:'M-65', optimum:8840, nm:11990 },
  // 20" — minimum torque only (position-based makeup)
  { size:'20"', weight:94, grade:'H-40', optimum:5810, nm:7880 },
  { size:'20"', weight:94, grade:'J-55', optimum:7830, nm:10620 },
  { size:'20"', weight:106.5, grade:'J-55', optimum:9130, nm:12380 },
  { size:'20"', weight:133, grade:'J-55', optimum:11920, nm:16160 },
  { size:'20"', weight:94, grade:'K-55', optimum:8230, nm:11160 },
  { size:'20"', weight:106.5, grade:'K-55', optimum:9590, nm:13000 },
  { size:'20"', weight:133, grade:'K-55', optimum:12520, nm:16970 },
  { size:'20"', weight:94, grade:'M-65', optimum:9180, nm:12450 },
  { size:'20"', weight:106.5, grade:'M-65', optimum:10700, nm:14510 },
];

// BTC: single estimated torque per SIZE GROUP (grade-independent)
// Source: TESCO MLT Ring Field Make-Up Handbook (API RP 5C1)
// NOTE: Position at base of triangle is the PRIMARY makeup criterion.
// Torque values are estimates only. Min = Max = Optimum for BTC (position governs).
/**
 * Named rather than written inline. An inline `{ size: ...; optimum: ... }[]`
 * annotation is what made this array read as 12 rows when it has 11 — the API's
 * count assertion matched `/\{\s*size:/g` against source text and counted the
 * type as a row. It has held 4-1/2" through 13-3/8" throughout. 16", 18-5/8" and
 * 20" are absent by design: position-governed, same reason as LARGE_STC_SIZES.
 */
type BtcRow = { size: string; optimum: number; nm: number };

export const BTC_ESTIMATED: BtcRow[] = [
  { size:'4-1/2"',  optimum:4500,  nm:6101  },
  { size:'5"',      optimum:5000,  nm:6779  },
  { size:'5-1/2"',  optimum:5500,  nm:7457  },
  { size:'6-5/8"',  optimum:6630,  nm:8990  },
  { size:'7"',      optimum:7000,  nm:9491  },
  { size:'7-5/8"',  optimum:7630,  nm:10345 },
  { size:'8-5/8"',  optimum:8630,  nm:11702 },
  { size:'9-5/8"',  optimum:9630,  nm:13059 },
  { size:'10-3/4"', optimum:10750, nm:14582 },
  { size:'11-3/4"', optimum:11750, nm:15938 },
  { size:'13-3/8"', optimum:13380, nm:18148 },
];

const SPEC_MAP: Record<string, SpecRow[]> = {
  LTC: LTC_SPEC,
  STC: STC_SPEC,
};

// ─── Tubing Types & Spec (API RP 5C1 Table 3) ───────────────────────────────

export type TubingGrade =
  | 'H-40'
  | 'J-55'
  | 'C-75'
  | 'L-80'
  | 'N-80'
  | 'C-90'
  | 'P-105';

export type TubingThread = 'NU' | 'EUE' | 'IJ';

export interface TubingRow {
  size: string;
  weight: number;
  grade: TubingGrade;
  thread: TubingThread;
  optimum: number;
  nm: number;
}

export const TUBING_SPEC: TubingRow[] = [
  // 1.050"  NU
  { size:'1.050"', weight:1.14, grade:'H-40', thread:'NU', optimum:140, nm:190 },
  { size:'1.050"', weight:1.14, grade:'J-55', thread:'NU', optimum:180, nm:240 },
  { size:'1.050"', weight:1.14, grade:'C-75', thread:'NU', optimum:230, nm:310 },
  { size:'1.050"', weight:1.14, grade:'L-80', thread:'NU', optimum:240, nm:330 },
  { size:'1.050"', weight:1.14, grade:'N-80', thread:'NU', optimum:250, nm:340 },
  { size:'1.050"', weight:1.14, grade:'C-90', thread:'NU', optimum:260, nm:350 },
  // 1.050"  EUE
  { size:'1.050"', weight:1.2, grade:'H-40', thread:'EUE', optimum:460, nm:620 },
  { size:'1.050"', weight:1.2, grade:'J-55', thread:'EUE', optimum:600, nm:810 },
  { size:'1.050"', weight:1.2, grade:'C-75', thread:'EUE', optimum:780, nm:1060 },
  { size:'1.050"', weight:1.2, grade:'L-80', thread:'EUE', optimum:810, nm:1100 },
  { size:'1.050"', weight:1.2, grade:'N-80', thread:'EUE', optimum:830, nm:1130 },
  { size:'1.050"', weight:1.2, grade:'C-90', thread:'EUE', optimum:880, nm:1190 },
  // 1.315"  NU
  { size:'1.315"', weight:1.7, grade:'H-40', thread:'NU', optimum:210, nm:280 },
  { size:'1.315"', weight:1.7, grade:'J-55', thread:'NU', optimum:270, nm:370 },
  { size:'1.315"', weight:1.7, grade:'C-75', thread:'NU', optimum:360, nm:490 },
  { size:'1.315"', weight:1.7, grade:'L-80', thread:'NU', optimum:370, nm:500 },
  { size:'1.315"', weight:1.7, grade:'N-80', thread:'NU', optimum:380, nm:520 },
  { size:'1.315"', weight:1.7, grade:'C-90', thread:'NU', optimum:400, nm:540 },
  // 1.315"  EUE
  { size:'1.315"', weight:1.8, grade:'H-40', thread:'EUE', optimum:440, nm:600 },
  { size:'1.315"', weight:1.8, grade:'J-55', thread:'EUE', optimum:570, nm:770 },
  { size:'1.315"', weight:1.8, grade:'C-75', thread:'EUE', optimum:740, nm:1000 },
  { size:'1.315"', weight:1.8, grade:'L-80', thread:'EUE', optimum:760, nm:1030 },
  { size:'1.315"', weight:1.8, grade:'N-80', thread:'EUE', optimum:790, nm:1070 },
  { size:'1.315"', weight:1.8, grade:'C-90', thread:'EUE', optimum:830, nm:1130 },
  // 1.315"  IJ
  { size:'1.315"', weight:1.72, grade:'H-40', thread:'IJ', optimum:310, nm:420 },
  { size:'1.315"', weight:1.72, grade:'J-55', thread:'IJ', optimum:400, nm:540 },
  { size:'1.315"', weight:1.72, grade:'C-75', thread:'IJ', optimum:520, nm:710 },
  { size:'1.315"', weight:1.72, grade:'L-80', thread:'IJ', optimum:530, nm:720 },
  { size:'1.315"', weight:1.72, grade:'N-80', thread:'IJ', optimum:550, nm:750 },
  { size:'1.315"', weight:1.72, grade:'C-90', thread:'IJ', optimum:580, nm:790 },
  // 1.660"  NU
  { size:'1.660"', weight:2.3, grade:'H-40', thread:'NU', optimum:270, nm:370 },
  { size:'1.660"', weight:2.3, grade:'J-55', thread:'NU', optimum:350, nm:470 },
  { size:'1.660"', weight:2.3, grade:'C-75', thread:'NU', optimum:460, nm:620 },
  { size:'1.660"', weight:2.3, grade:'L-80', thread:'NU', optimum:470, nm:640 },
  { size:'1.660"', weight:2.3, grade:'N-80', thread:'NU', optimum:490, nm:660 },
  { size:'1.660"', weight:2.3, grade:'C-90', thread:'NU', optimum:510, nm:690 },
  // 1.660"  EUE
  { size:'1.660"', weight:2.3, grade:'H-40', thread:'EUE', optimum:530, nm:720 },
  { size:'1.660"', weight:2.4, grade:'J-55', thread:'EUE', optimum:690, nm:940 },
  { size:'1.660"', weight:2.4, grade:'C-75', thread:'EUE', optimum:910, nm:1230 },
  { size:'1.660"', weight:2.4, grade:'L-80', thread:'EUE', optimum:940, nm:1270 },
  { size:'1.660"', weight:2.4, grade:'N-80', thread:'EUE', optimum:960, nm:1300 },
  { size:'1.660"', weight:2.4, grade:'C-90', thread:'EUE', optimum:1020, nm:1380 },
  // 1.660"  IJ
  { size:'1.660"', weight:2.1, grade:'H-40', thread:'IJ', optimum:380, nm:520 },
  { size:'1.660"', weight:2.33, grade:'H-40', thread:'IJ', optimum:380, nm:520 },
  { size:'1.660"', weight:2.1, grade:'J-55', thread:'IJ', optimum:500, nm:680 },
  { size:'1.660"', weight:2.33, grade:'J-55', thread:'IJ', optimum:500, nm:680 },
  { size:'1.660"', weight:2.33, grade:'C-75', thread:'IJ', optimum:650, nm:880 },
  { size:'1.660"', weight:2.33, grade:'L-80', thread:'IJ', optimum:680, nm:920 },
  { size:'1.660"', weight:2.33, grade:'N-80', thread:'IJ', optimum:690, nm:940 },
  { size:'1.660"', weight:2.33, grade:'C-90', thread:'IJ', optimum:730, nm:990 },
  // 1.900"  NU
  { size:'1.900"', weight:2.75, grade:'H-40', thread:'NU', optimum:320, nm:430 },
  { size:'1.900"', weight:2.75, grade:'J-55', thread:'NU', optimum:410, nm:560 },
  { size:'1.900"', weight:2.75, grade:'C-75', thread:'NU', optimum:540, nm:730 },
  { size:'1.900"', weight:2.75, grade:'L-80', thread:'NU', optimum:560, nm:760 },
  { size:'1.900"', weight:2.75, grade:'N-80', thread:'NU', optimum:570, nm:770 },
  { size:'1.900"', weight:2.75, grade:'C-90', thread:'NU', optimum:610, nm:830 },
  // 1.900"  EUE
  { size:'1.900"', weight:2.9, grade:'H-40', thread:'EUE', optimum:670, nm:910 },
  { size:'1.900"', weight:2.9, grade:'J-55', thread:'EUE', optimum:880, nm:1190 },
  { size:'1.900"', weight:2.9, grade:'C-75', thread:'EUE', optimum:1150, nm:1560 },
  { size:'1.900"', weight:2.9, grade:'L-80', thread:'EUE', optimum:1190, nm:1610 },
  { size:'1.900"', weight:2.9, grade:'N-80', thread:'EUE', optimum:1220, nm:1650 },
  { size:'1.900"', weight:2.9, grade:'C-90', thread:'EUE', optimum:1300, nm:1760 },
  // 1.900"  IJ
  { size:'1.900"', weight:2.4, grade:'H-40', thread:'IJ', optimum:450, nm:610 },
  { size:'1.900"', weight:2.76, grade:'H-40', thread:'IJ', optimum:450, nm:610 },
  { size:'1.900"', weight:2.4, grade:'J-55', thread:'IJ', optimum:580, nm:790 },
  { size:'1.900"', weight:2.76, grade:'J-55', thread:'IJ', optimum:580, nm:790 },
  { size:'1.900"', weight:2.76, grade:'C-75', thread:'IJ', optimum:760, nm:1030 },
  { size:'1.900"', weight:2.76, grade:'L-80', thread:'IJ', optimum:790, nm:1070 },
  { size:'1.900"', weight:2.76, grade:'N-80', thread:'IJ', optimum:810, nm:1100 },
  { size:'1.900"', weight:2.76, grade:'C-90', thread:'IJ', optimum:860, nm:1170 },
  // 2.063"  IJ
  { size:'2.063"', weight:3.25, grade:'H-40', thread:'IJ', optimum:570, nm:770 },
  { size:'2.063"', weight:3.25, grade:'J-55', thread:'IJ', optimum:740, nm:1000 },
  { size:'2.063"', weight:3.25, grade:'C-75', thread:'IJ', optimum:970, nm:1320 },
  { size:'2.063"', weight:3.25, grade:'L-80', thread:'IJ', optimum:1010, nm:1370 },
  { size:'2.063"', weight:3.25, grade:'N-80', thread:'IJ', optimum:1030, nm:1400 },
  { size:'2.063"', weight:3.25, grade:'C-90', thread:'IJ', optimum:1100, nm:1490 },
  // 2-3/8"  NU
  { size:'2-3/8"', weight:4, grade:'H-40', thread:'NU', optimum:470, nm:640 },
  { size:'2-3/8"', weight:4.6, grade:'H-40', thread:'NU', optimum:560, nm:760 },
  { size:'2-3/8"', weight:4, grade:'J-55', thread:'NU', optimum:610, nm:830 },
  { size:'2-3/8"', weight:4.6, grade:'J-55', thread:'NU', optimum:730, nm:990 },
  { size:'2-3/8"', weight:4, grade:'C-75', thread:'NU', optimum:800, nm:1080 },
  { size:'2-3/8"', weight:4.6, grade:'C-75', thread:'NU', optimum:960, nm:1300 },
  { size:'2-3/8"', weight:5.8, grade:'C-75', thread:'NU', optimum:1380, nm:1870 },
  { size:'2-3/8"', weight:4, grade:'L-80', thread:'NU', optimum:830, nm:1130 },
  { size:'2-3/8"', weight:4.6, grade:'L-80', thread:'NU', optimum:990, nm:1340 },
  { size:'2-3/8"', weight:5.8, grade:'L-80', thread:'NU', optimum:1420, nm:1930 },
  { size:'2-3/8"', weight:4, grade:'N-80', thread:'NU', optimum:850, nm:1150 },
  { size:'2-3/8"', weight:4.6, grade:'N-80', thread:'NU', optimum:1020, nm:1380 },
  { size:'2-3/8"', weight:5.8, grade:'N-80', thread:'NU', optimum:1460, nm:1980 },
  { size:'2-3/8"', weight:4, grade:'C-90', thread:'NU', optimum:910, nm:1230 },
  { size:'2-3/8"', weight:4.6, grade:'C-90', thread:'NU', optimum:1080, nm:1460 },
  { size:'2-3/8"', weight:5.8, grade:'C-90', thread:'NU', optimum:1550, nm:2100 },
  { size:'2-3/8"', weight:4.6, grade:'P-105', thread:'NU', optimum:1280, nm:1740 },
  { size:'2-3/8"', weight:5.8, grade:'P-105', thread:'NU', optimum:1840, nm:2490 },
  // 2-3/8"  EUE
  { size:'2-3/8"', weight:4.7, grade:'H-40', thread:'EUE', optimum:990, nm:1340 },
  { size:'2-3/8"', weight:4.7, grade:'J-55', thread:'EUE', optimum:1290, nm:1750 },
  { size:'2-3/8"', weight:4.7, grade:'C-75', thread:'EUE', optimum:1700, nm:2300 },
  { size:'2-3/8"', weight:5.95, grade:'C-75', thread:'EUE', optimum:2120, nm:2870 },
  { size:'2-3/8"', weight:4.7, grade:'L-80', thread:'EUE', optimum:1760, nm:2390 },
  { size:'2-3/8"', weight:5.95, grade:'L-80', thread:'EUE', optimum:2190, nm:2970 },
  { size:'2-3/8"', weight:4.7, grade:'N-80', thread:'EUE', optimum:1800, nm:2440 },
  { size:'2-3/8"', weight:5.95, grade:'N-80', thread:'EUE', optimum:2240, nm:3040 },
  { size:'2-3/8"', weight:4.7, grade:'C-90', thread:'EUE', optimum:1920, nm:2600 },
  { size:'2-3/8"', weight:5.95, grade:'C-90', thread:'EUE', optimum:2390, nm:3240 },
  { size:'2-3/8"', weight:4.7, grade:'P-105', thread:'EUE', optimum:2270, nm:3080 },
  { size:'2-3/8"', weight:5.95, grade:'P-105', thread:'EUE', optimum:2830, nm:3840 },
  // 2-7/8"  NU
  { size:'2-7/8"', weight:6.4, grade:'H-40', thread:'NU', optimum:900, nm:1220 },
  { size:'2-7/8"', weight:6.4, grade:'J-55', thread:'NU', optimum:1050, nm:1420 },
  { size:'2-7/8"', weight:6.4, grade:'C-75', thread:'NU', optimum:1380, nm:1870 },
  { size:'2-7/8"', weight:7.8, grade:'C-75', thread:'NU', optimum:1850, nm:2510 },
  { size:'2-7/8"', weight:8.6, grade:'C-75', thread:'NU', optimum:2090, nm:2830 },
  { size:'2-7/8"', weight:6.4, grade:'L-80', thread:'NU', optimum:1430, nm:1940 },
  { size:'2-7/8"', weight:7.8, grade:'L-80', thread:'NU', optimum:1910, nm:2590 },
  { size:'2-7/8"', weight:8.6, grade:'L-80', thread:'NU', optimum:2160, nm:2930 },
  { size:'2-7/8"', weight:6.4, grade:'N-80', thread:'NU', optimum:1470, nm:1990 },
  { size:'2-7/8"', weight:7.8, grade:'N-80', thread:'NU', optimum:1960, nm:2660 },
  { size:'2-7/8"', weight:8.6, grade:'N-80', thread:'NU', optimum:2210, nm:3000 },
  { size:'2-7/8"', weight:6.4, grade:'C-90', thread:'NU', optimum:1570, nm:2130 },
  { size:'2-7/8"', weight:7.8, grade:'C-90', thread:'NU', optimum:2090, nm:2830 },
  { size:'2-7/8"', weight:8.6, grade:'C-90', thread:'NU', optimum:2370, nm:3210 },
  { size:'2-7/8"', weight:6.4, grade:'P-105', thread:'NU', optimum:1850, nm:2510 },
  { size:'2-7/8"', weight:7.8, grade:'P-105', thread:'NU', optimum:2470, nm:3350 },
  { size:'2-7/8"', weight:8.6, grade:'P-105', thread:'NU', optimum:2790, nm:3780 },
  // 2-7/8"  EUE
  { size:'2-7/8"', weight:6.5, grade:'H-40', thread:'EUE', optimum:1250, nm:1690 },
  { size:'2-7/8"', weight:6.5, grade:'J-55', thread:'EUE', optimum:1650, nm:2240 },
  { size:'2-7/8"', weight:6.5, grade:'C-75', thread:'EUE', optimum:2170, nm:2940 },
  { size:'2-7/8"', weight:7.9, grade:'C-75', thread:'EUE', optimum:2610, nm:3540 },
  { size:'2-7/8"', weight:8.7, grade:'C-75', thread:'EUE', optimum:2850, nm:3860 },
  { size:'2-7/8"', weight:6.5, grade:'L-80', thread:'EUE', optimum:2250, nm:3050 },
  { size:'2-7/8"', weight:7.9, grade:'L-80', thread:'EUE', optimum:2710, nm:3670 },
  { size:'2-7/8"', weight:8.7, grade:'L-80', thread:'EUE', optimum:2950, nm:4000 },
  { size:'2-7/8"', weight:6.5, grade:'N-80', thread:'EUE', optimum:2300, nm:3120 },
  { size:'2-7/8"', weight:7.9, grade:'N-80', thread:'EUE', optimum:2770, nm:3760 },
  { size:'2-7/8"', weight:8.7, grade:'N-80', thread:'EUE', optimum:3020, nm:4090 },
  { size:'2-7/8"', weight:6.5, grade:'C-90', thread:'EUE', optimum:2460, nm:3340 },
  { size:'2-7/8"', weight:7.9, grade:'C-90', thread:'EUE', optimum:2970, nm:4030 },
  { size:'2-7/8"', weight:8.7, grade:'C-90', thread:'EUE', optimum:3230, nm:4380 },
  { size:'2-7/8"', weight:6.5, grade:'P-105', thread:'EUE', optimum:2910, nm:3950 },
  { size:'2-7/8"', weight:7.9, grade:'P-105', thread:'EUE', optimum:3500, nm:4750 },
  { size:'2-7/8"', weight:8.7, grade:'P-105', thread:'EUE', optimum:3810, nm:5170 },
  // 3-1/2"  NU
  { size:'3-1/2"', weight:7.7, grade:'H-40', thread:'NU', optimum:920, nm:1250 },
  { size:'3-1/2"', weight:9.2, grade:'H-40', thread:'NU', optimum:1120, nm:1520 },
  { size:'3-1/2"', weight:10.2, grade:'H-40', thread:'NU', optimum:1310, nm:1780 },
  { size:'3-1/2"', weight:7.7, grade:'J-55', thread:'NU', optimum:1210, nm:1640 },
  { size:'3-1/2"', weight:9.2, grade:'J-55', thread:'NU', optimum:1480, nm:2010 },
  { size:'3-1/2"', weight:10.2, grade:'J-55', thread:'NU', optimum:1720, nm:2330 },
  { size:'3-1/2"', weight:7.7, grade:'C-75', thread:'NU', optimum:1600, nm:2170 },
  { size:'3-1/2"', weight:9.2, grade:'C-75', thread:'NU', optimum:1950, nm:2640 },
  { size:'3-1/2"', weight:10.2, grade:'C-75', thread:'NU', optimum:2270, nm:3080 },
  { size:'3-1/2"', weight:12.7, grade:'C-75', thread:'NU', optimum:3030, nm:4110 },
  { size:'3-1/2"', weight:7.7, grade:'L-80', thread:'NU', optimum:1660, nm:2250 },
  { size:'3-1/2"', weight:9.2, grade:'L-80', thread:'NU', optimum:2030, nm:2750 },
  { size:'3-1/2"', weight:10.2, grade:'L-80', thread:'NU', optimum:2360, nm:3200 },
  { size:'3-1/2"', weight:12.7, grade:'L-80', thread:'NU', optimum:3140, nm:4260 },
  { size:'3-1/2"', weight:7.7, grade:'N-80', thread:'NU', optimum:1700, nm:2300 },
  { size:'3-1/2"', weight:9.2, grade:'N-80', thread:'NU', optimum:2070, nm:2810 },
  { size:'3-1/2"', weight:10.2, grade:'N-80', thread:'NU', optimum:2410, nm:3270 },
  { size:'3-1/2"', weight:12.7, grade:'N-80', thread:'NU', optimum:3210, nm:4350 },
  { size:'3-1/2"', weight:7.7, grade:'C-90', thread:'NU', optimum:1820, nm:2470 },
  { size:'3-1/2"', weight:9.2, grade:'C-90', thread:'NU', optimum:2220, nm:3010 },
  { size:'3-1/2"', weight:10.2, grade:'C-90', thread:'NU', optimum:2590, nm:3510 },
  { size:'3-1/2"', weight:12.7, grade:'C-90', thread:'NU', optimum:3440, nm:4660 },
  { size:'3-1/2"', weight:9.2, grade:'P-105', thread:'NU', optimum:2620, nm:3550 },
  { size:'3-1/2"', weight:12.7, grade:'P-105', thread:'NU', optimum:4060, nm:5500 },
  // 3-1/2"  EUE
  { size:'3-1/2"', weight:9.3, grade:'H-40', thread:'EUE', optimum:1730, nm:2350 },
  { size:'3-1/2"', weight:9.3, grade:'J-55', thread:'EUE', optimum:2280, nm:3090 },
  { size:'3-1/2"', weight:9.3, grade:'C-75', thread:'EUE', optimum:3010, nm:4080 },
  { size:'3-1/2"', weight:12.95, grade:'C-75', thread:'EUE', optimum:4040, nm:5480 },
  { size:'3-1/2"', weight:9.3, grade:'L-80', thread:'EUE', optimum:3030, nm:4110 },
  { size:'3-1/2"', weight:12.95, grade:'L-80', thread:'EUE', optimum:4200, nm:5690 },
  { size:'3-1/2"', weight:9.3, grade:'N-80', thread:'EUE', optimum:3200, nm:4340 },
  { size:'3-1/2"', weight:12.95, grade:'N-80', thread:'EUE', optimum:4290, nm:5820 },
  { size:'3-1/2"', weight:9.3, grade:'C-90', thread:'EUE', optimum:3430, nm:4650 },
  { size:'3-1/2"', weight:12.95, grade:'C-90', thread:'EUE', optimum:4610, nm:6250 },
  { size:'3-1/2"', weight:9.3, grade:'P-105', thread:'EUE', optimum:4050, nm:5490 },
  { size:'3-1/2"', weight:12.95, grade:'P-105', thread:'EUE', optimum:5430, nm:7360 },
  // 4"  NU
  { size:'4"', weight:9.5, grade:'H-40', thread:'NU', optimum:930, nm:1260 },
  { size:'4"', weight:9.5, grade:'J-55', thread:'NU', optimum:1220, nm:1650 },
  { size:'4"', weight:9.5, grade:'C-75', thread:'NU', optimum:1620, nm:2200 },
  { size:'4"', weight:9.5, grade:'L-80', thread:'NU', optimum:1680, nm:2280 },
  { size:'4"', weight:9.5, grade:'N-80', thread:'NU', optimum:1720, nm:2330 },
  { size:'4"', weight:9.5, grade:'C-90', thread:'NU', optimum:1950, nm:2640 },
  // 4"  EUE
  { size:'4"', weight:11, grade:'H-40', thread:'EUE', optimum:1940, nm:2630 },
  { size:'4"', weight:11, grade:'J-55', thread:'EUE', optimum:2560, nm:3470 },
  { size:'4"', weight:11, grade:'C-75', thread:'EUE', optimum:3390, nm:4600 },
  { size:'4"', weight:11, grade:'L-80', thread:'EUE', optimum:3530, nm:4790 },
  { size:'4"', weight:11, grade:'N-80', thread:'EUE', optimum:3600, nm:4880 },
  { size:'4"', weight:11, grade:'C-90', thread:'EUE', optimum:3870, nm:5250 },
  // 4-1/2"  NU
  { size:'4-1/2"', weight:12.6, grade:'H-40', thread:'NU', optimum:1320, nm:1790 },
  { size:'4-1/2"', weight:12.6, grade:'J-55', thread:'NU', optimum:1740, nm:2360 },
  { size:'4-1/2"', weight:12.6, grade:'C-75', thread:'NU', optimum:2300, nm:3120 },
  { size:'4-1/2"', weight:12.6, grade:'L-80', thread:'NU', optimum:2400, nm:3250 },
  { size:'4-1/2"', weight:12.6, grade:'N-80', thread:'NU', optimum:2440, nm:3310 },
  { size:'4-1/2"', weight:12.6, grade:'C-90', thread:'NU', optimum:2630, nm:3570 },
  // 4-1/2"  EUE
  { size:'4-1/2"', weight:12.75, grade:'H-40', thread:'EUE', optimum:2160, nm:2930 },
  { size:'4-1/2"', weight:12.75, grade:'J-55', thread:'EUE', optimum:2860, nm:3880 },
  { size:'4-1/2"', weight:12.75, grade:'C-75', thread:'EUE', optimum:3780, nm:5120 },
  { size:'4-1/2"', weight:12.75, grade:'L-80', thread:'EUE', optimum:3940, nm:5340 },
  { size:'4-1/2"', weight:12.75, grade:'N-80', thread:'EUE', optimum:4020, nm:5450 },
  { size:'4-1/2"', weight:12.75, grade:'C-90', thread:'EUE', optimum:4330, nm:5870 },
];

// ─── Tubing spec lookup ──────────────────────────────────────────────────────

export function lookupTubingSpec(
  size: string,
  weightPpf: number,
  grade: TubingGrade,
  thread: TubingThread
): TorqueProfile | null {
  const row = TUBING_SPEC.find(
    (r) =>
      r.size === size &&
      Math.abs(r.weight - weightPpf) < 0.15 &&
      r.grade === grade &&
      r.thread === thread
  );
  if (!row) return null;

  return {
    size,
    weightPpf,
    grade: grade as unknown as CasingGrade,
    connectionType: thread as unknown as ConnectionType,
    torqueMin: Math.round(row.optimum * 0.75),
    torqueOptimum: row.optimum,
    torqueMax: Math.round(row.optimum * 1.25),
    torqueLowShoulder: null,
    torqueHighShoulder: null,
    shouldered: null,
    source: 'api_spec',
    sourceLabel: API_SPEC_TORQUE_LABEL,
    fromOfflineFallback: true,
  };
}

/**
 * Unique nominal weights (lb/ft) available for a tubing size across all threads,
 * sorted ascending. Drives the job form's tubing weight dropdown so it always
 * offers values that can resolve against the spec table.
 */
export function tubingWeightsForSize(size: string): number[] {
  if (!size) return [];
  const weights = new Set<number>();
  for (const row of TUBING_SPEC) {
    if (row.size === size) weights.add(row.weight);
  }
  return Array.from(weights).sort((a, b) => a - b);
}

// ─── Spec lookup ──────────────────────────────────────────────────────────────

/** Sizes where STC only specifies a minimum torque (no ±25% range) */
const LARGE_STC_SIZES = ['16"', '18-5/8"', '20"'];

function lookupSpec(
  size: string,
  weightPpf: number,
  grade: CasingGrade,
  connection: ConnectionType
): TorqueProfile | null {
  // BTC: size-only lookup (grade-independent, position-based makeup)
  if (connection === 'BTC') {
    const row = BTC_ESTIMATED.find((r) => r.size === size);
    if (!row) return null;
    return {
      size,
      weightPpf,
      grade,
      connectionType: connection,
      torqueMin: row.optimum,
      torqueOptimum: row.optimum,
      torqueMax: row.optimum,
      torqueLowShoulder: null,
      torqueHighShoulder: null,
      shouldered: null,
      source: 'api_spec',
      sourceLabel: API_SPEC_TORQUE_LABEL,
      fromOfflineFallback: true,
    };
  }

  // Sour-service grade aliases — resolve to base grade for spec lookup
  const GRADE_ALIASES: Partial<Record<CasingGrade, CasingGrade>> = {
    'SOO55': 'K-55',
    'PS-80': 'L-80',
    'TN 80SS': 'L-80',
  };
  const resolvedGrade = GRADE_ALIASES[grade] ?? grade;

  const table = SPEC_MAP[connection];
  if (!table) return null;

  const row = table.find(
    (r) =>
      r.size === size &&
      Math.abs(r.weight - weightPpf) < 0.15 &&
      r.grade === resolvedGrade
  );
  if (!row) return null;

  // Large sizes: minimum torque only (no ±25% range, position-based makeup)
  // STC: 16", 18-5/8", 20"   LTC: 20"
  const isLargePosition =
    (connection === 'STC' && LARGE_STC_SIZES.includes(size)) ||
    (connection === 'LTC' && size === '20"');

  return {
    size,
    weightPpf,
    grade,
    connectionType: connection,
    torqueMin: isLargePosition ? row.optimum : Math.round(row.optimum * 0.75),
    torqueOptimum: row.optimum,
    torqueMax: isLargePosition ? row.optimum : Math.round(row.optimum * 1.25),
    torqueLowShoulder: null,
    torqueHighShoulder: null,
    shouldered: null,
    source: 'api_spec',
    sourceLabel: API_SPEC_TORQUE_LABEL,
    fromOfflineFallback: true,
  };
}

// ─── Grade key normalization ─────────────────────────────────────────────────
// Was `grade.replace(/-/g, '')` — hyphens and nothing else. No trim, no
// whitespace collapse, no case fold, so 'TN 110S' and 'Tn110s' were two
// different keys for one pipe and the tenant ended up with two profiles 840
// ft-lb apart. It also MANUFACTURED duplicates: 'P110 - ICY' lost its hyphen
// and became 'P110  ICY' with two spaces, a key that had never existed.
//
// Storage is the canonical hyphenated form as of API migration 0287. The API
// canonicalizes every write and the GET query param, so it — not this file —
// is the authority; see the note in src/data/casingData.ts.

export { canonicalizeGrade, foldGradeKey } from '../../data/casingData';

export function toTorqueProfileGradeKey(grade: string): string {
  return canonicalizeGrade(grade) ?? '';
}

// ─── Weight normalization ───────────────────────────────────────────────────
// Normalize weight_ppf to 2 decimal places so "11.6" and "11.60" always
// resolve to the same DB key.  Used by both the lookup query and the save
// payload — the single source of truth, same pattern as toTorqueProfileGradeKey.

export function normalizeWeightPpf(w: number): number {
  return Math.round(w * 100) / 100;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useTorqueProfile({
  size,
  weightPpf,
  grade,
  connectionType,
}: UseTorqueProfileOptions): UseTorqueProfileReturn {
  const [torque, setTorque] = useState<TorqueProfile | null>(null);
  const [status, setStatus] = useState<TorqueStatus>('idle');
  const [isOverriding, setIsOverriding] = useState(false);

  const isOnline = useSyncStore((s) => s.isOnline);

  // Normalize weight once so the lookup and the save use the same 2-decimal
  // value. The server normalizes too; this keeps the local fallback's lookup
  // agreeing with what the server would have matched.
  const normWeight = weightPpf !== null ? normalizeWeightPpf(weightPpf) : null;

  const isReady =
    !!size && normWeight !== null && normWeight > 0 && !!grade && !!connectionType;


  /**
   * The lookup key, sent as the form holds it.
   *
   * The grade is NOT pre-mangled — no hyphen strip, no case fold. The API
   * canonicalizes size, weight and grade on both the write and the read with one
   * helper, so a client that "helps" can only disagree with it. Stripping
   * hyphens here is the specific bug that made K-55 unrecallable and manufactured
   * 'P110  ICY' out of 'P110 - ICY'.
   */
  const lookupKey: TorqueLookupKey | null = useMemo(
    () =>
      isReady
        ? {
            size,
            weightPpf: normWeight!,
            grade: grade as string,
            connectionType: connectionType as string,
          }
        : null,
    // Memoised on the four combination fields. Without this the object is a new
    // identity on every render, so every callback holding it in its dependency
    // list is rebuilt, and the resolve effect re-fires on unrelated renders.
    [isReady, size, normWeight, grade, connectionType],
  );

  /**
   * Resolve from the LOCAL spec arrays. Offline only.
   *
   * This is the second copy of the API RP 5C1 library and it exists so a crew in
   * a dead zone still sees published torque. It cannot know about a company
   * override — those live server-side — so anything it returns is flagged
   * `fromOfflineFallback` and the screen says so.
   */
  const resolveOffline = useCallback((): TorqueProfile | null => {
    if (!isReady) return null;

    const spec = isTubingThread(connectionType)
      ? lookupTubingSpec(
          size,
          weightPpf!,
          grade as unknown as TubingGrade,
          String(connectionType).trim().toUpperCase() as TubingThread,
        )
      : lookupSpec(size, weightPpf!, grade as CasingGrade, connectionType as ConnectionType);

    if (!spec) return null;
    // `shouldered` null: the device cannot see a company flag, and the local
    // arrays carry no shoulder torques to infer one from.
    return { ...spec, shouldered: null, source: 'api_spec', fromOfflineFallback: true };
  }, [isReady, size, weightPpf, grade, connectionType]);

  /**
   * Ask the API, or fall back to the local arrays.
   *
   * Extracted from the effect so `revertToLibrary` can re-run exactly the same
   * resolution after a delete rather than assuming what the library holds.
   */
  const resolveNow = useCallback(
    async (signal?: AbortSignal): Promise<TorqueProfile | null> => {
      if (!lookupKey) return null;

      // Offline by the app's own detector — do not even attempt the request.
      // `isOnline` is the sync store's flag, the same one the offline queue and
      // the sync bar read, so torque agrees with the rest of the app about
      // whether there is a network.
      if (!isOnline) return resolveOffline();

      try {
        const res = await api.torqueProfiles.resolve(lookupKey, { signal });
        const d = res.data;

        return {
          size,
          weightPpf: normWeight!,
          grade: grade as CasingGrade,
          connectionType: connectionType as ConnectionType,
          torqueMin: d.torqueMin ?? null,
          torqueOptimum: d.torqueOptimum ?? null,
          torqueMax: d.torqueMax ?? null,
          torqueLowShoulder: d.torqueLowShoulder ?? null,
          torqueHighShoulder: d.torqueHighShoulder ?? null,
          // Verbatim, like the source. `?? null`, never `?? false`: an API that
          // predates the flag has not said "not shouldered".
          shouldered: typeof d.shouldered === 'boolean' ? d.shouldered : null,
          // Verbatim, both of them. The API decided which source won and how to
          // word it; re-deriving either here is how the screen and the call
          // sheet come to disagree.
          source: (d.source ?? null) as TorqueSource,
          sourceLabel: d.sourceLabel ?? '',
          fromOfflineFallback: false,
        };
      } catch (err: any) {
        // An aborted request is a superseded combination, not a failure.
        if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') throw err;
        // The network went away between the check and the request, or the API
        // is down. A read may degrade to the library; it may not degrade to a
        // blank field that looks like "this pipe has no torque".
        //
        // If the library has nothing either, the caller is told the LOOKUP
        // FAILED — not that the combination is unlisted. Those two states offer
        // the same open fields and mean opposite things: one says "no published
        // value exists for this pipe", the other says "we could not find out".
        // Wording the second as the first invites someone to invent a number
        // for a pipe the library actually covers.
        const fallback = resolveOffline();
        if (!fallback) throw err;
        return fallback;
      }
    },
    [lookupKey, isOnline, resolveOffline, size, normWeight, grade, connectionType],
  );

  /**
   * RE-RESOLVE ON EVERY COMBINATION CHANGE.
   *
   * Deps are the four combination fields (via the normalized weight) and
   * nothing else. Deliberately NOT the torque values: they change on every
   * keystroke while someone is typing an override, and re-running this would
   * wipe the entry mid-edit.
   *
   * The previous combination's values must never survive into the new one. A
   * carried-over number is an over-torque risk, so state is cleared before the
   * request rather than after it resolves — a slow answer must not leave the
   * old numbers on screen next to the new pipe spec.
   */
  const requestId = useRef(0);

  useEffect(() => {
    const id = ++requestId.current;

    setIsOverriding(false);
    setTorque(null);

    if (!isReady) {
      setStatus('idle');
      return;
    }

    setStatus('loading');
    const controller = new AbortController();

    resolveNow(controller.signal)
      .then((resolved) => {
        // A late answer for a combination the user has already moved off must
        // not land. The abort covers the request; this covers the gap between
        // the response arriving and this callback running.
        if (id !== requestId.current) return;
        setTorque(resolved);
        setStatus('resolved');
      })
      .catch((err: any) => {
        if (id !== requestId.current) return;
        if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
        setTorque(null);
        setStatus('error');
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size, normWeight, grade, connectionType, isReady, isOnline]);

  /**
   * Editable when there is nothing to show, or when the user asked to override.
   *
   * `source === null` is the miss case — the library does not carry this
   * combination and the tenant has not saved one. It opens for typing
   * immediately: this is TNT's 8-5/8" 28 J-55 LT&C, and the old behaviour was a
   * read-only blank with no way forward.
   *
   * An offline fallback value is NOT editable. Saving needs the network, and a
   * Save button that cannot reach the server is a promise the app cannot keep —
   * a queued torque profile is not the same class of thing as a queued ticket.
   */
  const isEditable =
    // 'error' counts. A failed lookup must not lock a crew out of entering the
    // manufacturer's numbers — the save goes to the server and surfaces its own
    // error if that is still down. Blocking entry because a read failed is how
    // a dead API becomes a stopped job.
    (status === 'resolved' || status === 'error') &&
    !torque?.fromOfflineFallback &&
    (torque === null || torque.source === null || isOverriding);

  const canRevert = torque?.source === 'company' && !torque.fromOfflineFallback;

  // The saved flag wins; the known list only stands in where there is none.
  const hasShoulders = typeof torque?.shouldered === 'boolean'
    ? torque.shouldered
    : isKnownShoulderedConnection(connectionType);

  const beginOverride = useCallback(() => setIsOverriding(true), []);
  const cancelOverride = useCallback(() => setIsOverriding(false), []);

  const saveTorqueProfile = useCallback(
    async (values: Partial<TorqueProfile>) => {
      if (!lookupKey) return;
      const body: TorqueProfilePayload = {
        size: lookupKey.size,
        weight_ppf: lookupKey.weightPpf,
        // As typed. Hyphens intact — see the note on lookupKey.
        grade: lookupKey.grade,
        connection_type: lookupKey.connectionType,
        torque_min: values.torqueMin ?? torque?.torqueMin ?? null,
        torque_optimum: values.torqueOptimum ?? torque?.torqueOptimum ?? null,
        torque_max: values.torqueMax ?? torque?.torqueMax ?? null,
        torque_low_shoulder: values.torqueLowShoulder ?? torque?.torqueLowShoulder ?? null,
        torque_high_shoulder: values.torqueHighShoulder ?? torque?.torqueHighShoulder ?? null,
      };
      // Sent only when the caller decided it. Absent, the API defaults a new
      // combo from its list and keeps an existing combo's stored flag.
      if (typeof values.shouldered === 'boolean') {
        body.shouldered = values.shouldered;
        // Not shouldered means no shoulder torques; the API nulls them anyway,
        // and sending them would only make the payload claim what is not saved.
        if (!values.shouldered) {
          body.torque_low_shoulder = null;
          body.torque_high_shoulder = null;
        }
      }

      // Through apiClient, NOT a raw fetch. `fetch()` resolves normally on a
      // 403/400/500 — it only rejects on a network-level failure — so an
      // unchecked `await fetch(...)` puts a rejected save on the identical code
      // path as a stored one. Dutch Casing Services lost a custom grade that
      // way: no error, no retry prompt, and a crew running pipe to whatever
      // numbers were there before.
      //
      // This rejects on failure. Nothing below it runs, so no state update
      // claims a save the server did not make.
      await api.torqueProfiles.save(body);

      // Re-resolve rather than assuming the save's own numbers are now what
      // resolves. The server owns precedence and the label; asking it is the
      // only way the badge is guaranteed to match the call sheet.
      const resolved = await resolveNow();
      setTorque(resolved);
      setIsOverriding(false);
      setStatus('resolved');
    },
    [lookupKey, torque, resolveNow],
  );

  const refresh = useCallback(async () => {
    const resolved = await resolveNow();
    setTorque(resolved);
    setIsOverriding(false);
    setStatus('resolved');
  }, [resolveNow]);

  const revertToLibrary = useCallback(async () => {
    if (!lookupKey) return;

    // Hard delete (API #314). Not a POST of nulls: a null-valued row would look
    // reverted while still owning the save's upsert key and printing blanks to
    // anything reading the table directly.
    await api.torqueProfiles.remove(lookupKey);

    const resolved = await resolveNow();
    setTorque(resolved);
    setIsOverriding(false);
    setStatus('resolved');
  }, [lookupKey, resolveNow]);

  return {
    torque,
    status,
    hasShoulders,
    isEditable,
    isOverriding,
    beginOverride,
    cancelOverride,
    canRevert,
    saveTorqueProfile,
    refresh,
    revertToLibrary,
  };
}

/*
─── PostgreSQL Migration ────────────────────────────────────────────────────────

-- Run this in your Railway PostgreSQL instance

CREATE TABLE torque_profiles (
  id              UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  -- Lookup key (unique constraint — one profile per combo)
  size            TEXT NOT NULL,
  weight_ppf      NUMERIC(6,2) NOT NULL,
  grade           TEXT NOT NULL,
  connection_type TEXT NOT NULL,
  -- Torque values (ft-lb)
  torque_min         INTEGER,
  torque_optimum     INTEGER,
  torque_max         INTEGER,
  -- Shoulder torques — NULL for non-shouldered connections
  torque_low_shoulder  INTEGER,
  torque_high_shoulder INTEGER,
  -- Metadata
  created_at      TIMESTAMPTZ NOT NULL DEFAULT NOW(),
  updated_at      TIMESTAMPTZ NOT NULL DEFAULT NOW(),
  created_by      UUID REFERENCES users(id),
  -- Unique on the 4-field combo — global across all clients/rigs
  CONSTRAINT uq_torque_profile UNIQUE (size, weight_ppf, grade, connection_type)
);

CREATE INDEX idx_torque_profiles_lookup
  ON torque_profiles (size, weight_ppf, grade, connection_type);

-- Auto-update updated_at
CREATE TRIGGER trg_torque_profiles_updated_at
  BEFORE UPDATE ON torque_profiles
  FOR EACH ROW EXECUTE FUNCTION update_updated_at_column();


─── Express API Routes (Node/Express backend) ──────────────────────────────────

// GET /api/torque-profiles?size=7"&weight_ppf=23&grade=P110&connection_type=VAM+TOP
router.get('/torque-profiles', async (req, res) => {
  const { size, weight_ppf, grade, connection_type } = req.query;
  const row = await db.query(
    `SELECT * FROM torque_profiles
     WHERE size = $1
       AND weight_ppf = $2
       AND grade = $3
       AND connection_type = $4
     LIMIT 1`,
    [size, parseFloat(weight_ppf), grade, connection_type]
  );
  if (!row.rows[0]) return res.status(404).json({ error: 'not found' });
  res.json(row.rows[0]);
});

// POST /api/torque-profiles  (upsert — insert or update on conflict)
router.post('/torque-profiles', requireAuth, async (req, res) => {
  const {
    size, weight_ppf, grade, connection_type,
    torque_min, torque_optimum, torque_max,
    torque_low_shoulder, torque_high_shoulder,
  } = req.body;
  const row = await db.query(
    `INSERT INTO torque_profiles
       (size, weight_ppf, grade, connection_type,
        torque_min, torque_optimum, torque_max,
        torque_low_shoulder, torque_high_shoulder, created_by)
     VALUES ($1,$2,$3,$4,$5,$6,$7,$8,$9,$10)
     ON CONFLICT (size, weight_ppf, grade, connection_type)
     DO UPDATE SET
       torque_min           = EXCLUDED.torque_min,
       torque_optimum       = EXCLUDED.torque_optimum,
       torque_max           = EXCLUDED.torque_max,
       torque_low_shoulder  = EXCLUDED.torque_low_shoulder,
       torque_high_shoulder = EXCLUDED.torque_high_shoulder,
       updated_at           = NOW()
     RETURNING *`,
    [size, weight_ppf, grade, connection_type,
     torque_min, torque_optimum, torque_max,
     torque_low_shoulder ?? null, torque_high_shoulder ?? null,
     req.user.id]
  );
  res.json(row.rows[0]);
});
*/
